"use client";
import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Menu,
  Tooltip,
  MenuItem,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  DashboardCustomize,
  ShowChart,
  MoveToInbox,
  Person as PersonIcon,
  BarChart as BarChartIcon,
  CalendarToday,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";
import usePermissionDataStore from "../../Zustand/Store/usePermissionDataStore";
import { emsRowMatchesLoggedInTrainer } from "../../Utils/emsTrainerUserMatch";
import { navigateToResourceRole } from "../Users/resourceRoleConfig";
import CounsellorDashboard from "../EmsOptimized/CounsellorDashboard";
import {
  resolveActiveWorkspace,
  userHasRoleName,
  WORKSPACES,
} from "../../Utils/workspaces";
import ListPersonAvatar, {
  buildListProfileImageUrl,
  buildTrainerPersonPhotoIndex,
  resolveTrainerProfileImageFromRow,
} from "../../Components/Table/ListPersonAvatar";
import * as XLSX from "xlsx";
import {
  UserCog, Boxes, GraduationCap, Target,
  UserPlus, CalendarClock,
  Wallet, AlertCircle, Download, BookOpen, Building2, IndianRupee,
  ClipboardList,
} from "lucide-react";
import {
  resolveOrganizationFromUserData,
  resolveOrganizationId,
} from "../../Utils/organizationSession";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  CartesianGrid,
} from "recharts";

const renderMonthlyStandingBar = ({ x, y, width, height, fill }) => {
  if (
    !Number.isFinite(height) ||
    height <= 0 ||
    !Number.isFinite(width) ||
    width <= 0
  ) {
    return null;
  }

  const radius = Math.min(6, width / 2, height);
  return (
    <path
      d={`M${x},${y + height} L${x},${y + radius} Q${x},${y} ${x + radius},${y} L${x + width - radius},${y} Q${x + width},${y} ${x + width},${y + radius} L${x + width},${y + height} Z`}
      fill={fill}
    />
  );
};

const MONTHLY_CHART_GRID_PROPS = {
  strokeDasharray: "3 3",
  stroke: "var(--ems-border)",
  vertical: true,
  horizontal: true,
};

const MONTHLY_CHART_X_AXIS_PROPS = {
  tick: { fontSize: 11, fill: "var(--ems-muted)" },
  axisLine: { stroke: "var(--ems-border)" },
  tickLine: { stroke: "var(--ems-border-strong)", strokeWidth: 1 },
  interval: 0,
  angle: -40,
  textAnchor: "end",
  height: 58,
  minTickGap: 0,
};

const normalizeDashboardStatus = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

/** Dashboard trainer-details: only Ongoing batches (not batch list pages). */
const isDashboardOngoingBatchClassRow = (row) =>
  normalizeDashboardStatus(row?.batch?.status ?? row?.status) === "ongoing";

/** Dashboard trainer-details: Pursuing students on Ongoing batches only. */
const isDashboardPursuingStudentOnOngoingBatch = (row) =>
  normalizeDashboardStatus(row?.batch_status) === "pursuing" &&
  normalizeDashboardStatus(row?.batch?.status) === "ongoing";

export default function Dashboard() {
  const navigate = useNavigate();
  const { userData } = useAuthStore();
  const activeWorkspace = resolveActiveWorkspace(userData);
  const isTrainerWorkspace = activeWorkspace === WORKSPACES.TRAINER;
  const isCounsellorUser = userHasRoleName(userData, [
    "counsellor",
    "counselor",
  ]);
  const isAccountantUser = userHasRoleName(userData, ["accountant"]);
  const org = React.useMemo(
    () => resolveOrganizationFromUserData(userData),
    [userData]
  );
  const organizationId = React.useMemo(
    () => resolveOrganizationId(userData),
    [userData]
  );
  const [employees, setEmployees] = React.useState([]);
  const [interns, setInterns] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  // Lead summary (replaces other dashboard widgets)
  const [leadsSummary, setLeadsSummary] = React.useState(null);
  const [leadsSummaryLoading, setLeadsSummaryLoading] =
    React.useState(false);
  const [leadsSummaryError, setLeadsSummaryError] = React.useState(null);
  const [feeInstallmentDueCounts, setFeeInstallmentDueCounts] = React.useState({
    monthPending: null,
    todayPending: null,
    overdue: null,
    monthPendingAmount: null,
    todayPendingAmount: null,
    overdueAmount: null,
  });

  /** Batch trainer → batch details (dashboard Fees section) */
  const [batchTrainerList, setBatchTrainerList] = React.useState([]);
  const [batchClassTrainers, setBatchClassTrainers] = React.useState([]);
  const [batchTrainerListLoading, setBatchTrainerListLoading] =
    React.useState(false);
  const [selectedBatchTrainerId, setSelectedBatchTrainerId] =
    React.useState("");
  const [batchDetails, setBatchDetails] = React.useState(null);
  const [batchDetailsLoading, setBatchDetailsLoading] = React.useState(false);
  const [batchDetailsError, setBatchDetailsError] = React.useState(null);
  /** `null` = loading; map `organization_ems_batch_id` → enrolled student count */
  const [batchStudentCountByBatchId, setBatchStudentCountByBatchId] =
    React.useState(null);
  /** 'all' | number — category index from `training_program_graph.categories` */
  const [trainingGraphCategory, setTrainingGraphCategory] =
    React.useState("all");

  const { Permission, setPermission } = usePermissionDataStore();

  const permList = React.useMemo(
    () => (Array.isArray(Permission) ? Permission : []),
    [Permission]
  );
  const hasEmsDashboardSection = React.useCallback(
    (sectionKey) => permList.includes(sectionKey),
    [permList]
  );
  const [onLeaveTodayApiCount, setOnLeaveTodayApiCount] = React.useState(null);

  const [apiUrl, setApiUrl] = React.useState(null);
  const [internsApiUrl, setInternsApiUrl] = React.useState(null);

  const [freelancers, setFreelancers] = React.useState([]);
  const [freelancersApiUrl, setFreelancersApiUrl] = React.useState(null);

  React.useEffect(() => {
    if (organizationId) {
      setApiUrl(
        `${MAIN_URL}/api/organizations/${organizationId}/filter-employee`
      );
      setInternsApiUrl(
        `${MAIN_URL}/api/organizations/${organizationId}/filter/intern`
      );
      setFreelancersApiUrl(
        `${MAIN_URL}/api/organizations/${organizationId}/freelance-employees`
      );
    }
  }, [organizationId]);

  // Keep Fee cards consistent with fee-installment list filters.
  React.useEffect(() => {
    if (!organizationId || !hasEmsDashboardSection("Fees_Collected")) {
      setFeeInstallmentDueCounts({
        monthPending: null,
        todayPending: null,
        overdue: null,
        monthPendingAmount: null,
        todayPendingAmount: null,
        overdueAmount: null,
      });
      return;
    }

    let isActive = true;
    const loadFeeInstallmentCounts = async () => {
      try {
        const token = localStorage.getItem("token");
        const url = `${MAIN_URL}/api/ems/fee-installments?organization_id=${organizationId}&per_page=all`;
        const res = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          mode: "cors",
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status} for ${url}`);
        }
        const json = await res.json();
        const rows = Array.isArray(json?.data?.data)
          ? json.data.data
          : Array.isArray(json?.data)
            ? json.data
            : [];

        const now = new Date();
        const todayStart = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate()
        );
        const todayEnd = new Date(todayStart);
        todayEnd.setHours(23, 59, 59, 999);
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

        const toDate = (v) => {
          if (!v) return null;
          const s = String(v).trim();
          if (!s) return null;
          const iso = s.length >= 10 ? s.slice(0, 10) : s;
          const d = new Date(iso);
          return Number.isNaN(d.getTime()) ? null : d;
        };
        const statusOf = (r) => String(r?.status ?? "").trim().toLowerCase();
        const installmentAmountInr = (r) => {
          const value = Number(
            r?.fee_instalment_inr ?? r?.fee_installment_inr ?? 0
          );
          return Number.isFinite(value) ? value : 0;
        };
        const sumInstallmentAmount = (list) =>
          list.reduce((sum, row) => sum + installmentAmountInr(row), 0);

        const monthPendingRows = rows.filter((r) => {
          const d = toDate(r?.due_date);
          const st = statusOf(r);
          return d && d >= monthStart && d <= monthEnd && st === "pending";
        });
        const todayPendingRows = rows.filter((r) => {
          const d = toDate(r?.due_date);
          const st = statusOf(r);
          return d && d >= todayStart && d <= todayEnd && st === "pending";
        });
        const overdueRows = rows.filter((r) => {
          const d = toDate(r?.due_date);
          const st = statusOf(r);
          return d && d < todayStart && st !== "paid" && st !== "cancelled";
        });

        if (!isActive) return;
        setFeeInstallmentDueCounts({
          monthPending: monthPendingRows.length,
          todayPending: todayPendingRows.length,
          overdue: overdueRows.length,
          monthPendingAmount: sumInstallmentAmount(monthPendingRows),
          todayPendingAmount: sumInstallmentAmount(todayPendingRows),
          overdueAmount: sumInstallmentAmount(overdueRows),
        });
      } catch (e) {
        if (!isActive) return;
        setFeeInstallmentDueCounts({
          monthPending: null,
          todayPending: null,
          overdue: null,
          monthPendingAmount: null,
          todayPendingAmount: null,
          overdueAmount: null,
        });
      }
    };

    loadFeeInstallmentCounts();
    return () => {
      isActive = false;
    };
  }, [organizationId, hasEmsDashboardSection]);

  React.useEffect(() => {
    if (!organizationId || !hasEmsDashboardSection("Current_Batches")) {
      setBatchTrainerList([]);
      setBatchClassTrainers([]);
      return;
    }
    let isActive = true;
    const load = async () => {
      setBatchTrainerListLoading(true);
      try {
        const token = localStorage.getItem("token");
        const headers = {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };
        const [classRes, trainerRes] = await Promise.all([
          fetch(
            `${MAIN_URL}/api/ems/batch-classes?organization_id=${organizationId}&per_page=all`,
            { method: "GET", headers, mode: "cors" }
          ),
          fetch(
            `${MAIN_URL}/api/ems/batch-class-trainers?organization_id=${organizationId}`,
            { method: "GET", headers, mode: "cors" }
          ),
        ]);
        const classJson = await classRes.json();
        const trainerJson = await trainerRes.json();
        const rows = Array.isArray(classJson?.data)
          ? classJson.data
          : Array.isArray(classJson?.data?.data)
            ? classJson.data.data
            : [];
        const trainerRows = Array.isArray(trainerJson?.data)
          ? trainerJson.data
          : Array.isArray(trainerJson?.data?.data)
            ? trainerJson.data.data
            : [];
        if (!isActive) return;
        setBatchTrainerList(rows.filter(isDashboardOngoingBatchClassRow));
        setBatchClassTrainers(trainerRows);
      } catch {
        if (isActive) {
          setBatchTrainerList([]);
          setBatchClassTrainers([]);
        }
      } finally {
        if (isActive) setBatchTrainerListLoading(false);
      }
    };
    load();
    return () => {
      isActive = false;
    };
  }, [organizationId, hasEmsDashboardSection]);

  React.useEffect(() => {
    if (!organizationId || !hasEmsDashboardSection("Current_Batches")) {
      setBatchStudentCountByBatchId(null);
      return;
    }
    let isActive = true;
    const load = async () => {
      setBatchStudentCountByBatchId(null);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(
          `${MAIN_URL}/api/ems/batch-students?organization_id=${organizationId}&per_page=all`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            mode: "cors",
          }
        );
        if (!res.ok) {
          if (isActive) setBatchStudentCountByBatchId({});
          return;
        }
        const json = await res.json();
        const rows = Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json?.data?.data)
            ? json.data.data
            : [];
        const counts = {};
        rows.forEach((r) => {
          if (!isDashboardPursuingStudentOnOngoingBatch(r)) return;
          const bid =
            r?.organization_ems_batch_id ?? r?.batch?.organization_ems_batch_id;
          if (bid == null || bid === "") return;
          const k = String(bid);
          counts[k] = (counts[k] || 0) + 1;
        });
        if (!isActive) return;
        setBatchStudentCountByBatchId(counts);
      } catch {
        if (isActive) setBatchStudentCountByBatchId({});
      }
    };
    load();
    return () => {
      isActive = false;
    };
  }, [organizationId, hasEmsDashboardSection]);

  React.useEffect(() => {
    if (!organizationId || !selectedBatchTrainerId) {
      setBatchDetails(null);
      setBatchDetailsError(null);
      return;
    }
    // Previously this dashboard called `/batch-details` using the selected id.
    // That endpoint expects `organization_ems_batch_trainer_id`.
    // Now the dropdown selection is based on `trainer_employee_id`,
    // so we derive batch details directly from `batchTrainerList` instead.
    setBatchDetails(null);
    setBatchDetailsError(null);
    setBatchDetailsLoading(false);
  }, [organizationId, selectedBatchTrainerId]);

  const resolveTrainerName = (item) => {
    const appUserName = String(
      item?.trainer?.applicationuser?.[0]?.full_name ??
        item?.trainer?.application_user?.[0]?.full_name ??
        item?.trainer?.application_user?.full_name ??
        ""
    )
      .replace(/\s+/g, " ")
      .trim();
    if (appUserName) return appUserName;

    const personFullName = String(
      item?.trainer?.organization_person?.person?.full_name ??
        item?.trainer?.organization_person?.Person?.full_name ??
        item?.trainer?.organizationPerson?.person?.full_name ??
        item?.trainer?.organizationPerson?.Person?.full_name ??
        item?.trainer?.person?.full_name ??
        item?.trainer?.Person?.full_name ??
        ""
    )
      .replace(/\s+/g, " ")
      .trim();
    if (personFullName) return personFullName;

    const fn =
      item?.trainer?.person?.first_name ??
      item?.trainer?.Person?.first_name ??
      item?.trainer?.organization_person?.person?.first_name ??
      item?.trainer?.organization_person?.Person?.first_name ??
      "";
    const ln =
      item?.trainer?.person?.last_name ??
      item?.trainer?.Person?.last_name ??
      item?.trainer?.organization_person?.person?.last_name ??
      item?.trainer?.organization_person?.Person?.last_name ??
      "";
    const combined = `${fn} ${ln}`.replace(/\s+/g, " ").trim();
    if (combined) return combined;

    return "Trainer";
  };

  const normalizePersonNameKey = React.useCallback((value) => {
    return String(value ?? "")
      .replace(/[\n\r\t]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  }, []);

  /** Employee photos keyed for trainer matching (org user / app user / name). */
  const trainerPhotoLookup = React.useMemo(() => {
    const byOrgUserId = new Map();
    const byAppUserId = new Map();
    const byName = new Map();
    (employees || []).forEach((emp) => {
      const url = buildListProfileImageUrl(
        emp?.profile_image_url ||
          emp?.org_persons?.person?.profile_image_url ||
          emp?.OrgPersons?.Person?.profile_image_url ||
          emp?.org_persons?.Person?.profile_image_url ||
          ""
      );
      if (!url) return;
      const orgUserId =
        emp?.organization_user_id ||
        emp?.org_persons?.organization_user_id ||
        emp?.OrgPersons?.organization_user_id;
      if (orgUserId != null && String(orgUserId).trim() !== "") {
        byOrgUserId.set(String(orgUserId), url);
      }
      const appUserId =
        emp?.application_user_id ||
        emp?.org_persons?.organization_user?.application_user_id ||
        emp?.OrgPersons?.OrganizationUser?.application_user_id;
      if (appUserId != null && String(appUserId).trim() !== "") {
        byAppUserId.set(String(appUserId), url);
      }
      const nameKey = normalizePersonNameKey(
        emp?.Name ||
          emp?.full_name ||
          emp?.name ||
          [emp?.first_name, emp?.middle_name, emp?.last_name]
            .filter(Boolean)
            .join(" ")
      );
      if (nameKey) byName.set(nameKey, url);

      const first = String(emp?.first_name || "").trim();
      const last = String(emp?.last_name || "").trim();
      if (first && last) {
        byName.set(normalizePersonNameKey(`${first} ${last}`), url);
      }
    });
    return { byOrgUserId, byAppUserId, byName };
  }, [employees, normalizePersonNameKey]);

  /** Reuse trainer.person photos already present on any batch-class row. */
  const trainerPersonPhotoIndex = React.useMemo(
    () =>
      buildTrainerPersonPhotoIndex(
        batchTrainerList,
        resolveTrainerName,
        normalizePersonNameKey
      ),
    [batchTrainerList, normalizePersonNameKey]
  );

  const resolveTrainerImageUrl = React.useCallback(
    (row) => {
      // 1) trainer.person / organization_person.person / trainer.profile_image_url
      const fromRow = resolveTrainerProfileImageFromRow(row);
      if (fromRow) return fromRow;

      const teid = row?.trainer_employee_id ?? row?.trainer?.organization_user_id;
      const appUserId =
        row?.trainer?.application_user_id ??
        row?.trainer?.applicationuser?.[0]?.application_user_id ??
        null;
      const nameKey = normalizePersonNameKey(resolveTrainerName(row));

      // 2) Same person photo from another batch-class row
      if (teid != null && String(teid).trim() !== "") {
        const shared =
          trainerPersonPhotoIndex.byOrgUserId.get(String(teid)) || "";
        if (shared) return shared;
      }
      if (appUserId != null && String(appUserId).trim() !== "") {
        const shared =
          trainerPersonPhotoIndex.byAppUserId.get(String(appUserId)) || "";
        if (shared) return shared;
      }
      if (nameKey && nameKey !== "trainer") {
        const shared = trainerPersonPhotoIndex.byName.get(nameKey) || "";
        if (shared) return shared;
      }

      // 3) Employee Person photo — shared by application_user_id (Shared Resource)
      if (appUserId != null && String(appUserId).trim() !== "") {
        const byApp = trainerPhotoLookup.byAppUserId.get(String(appUserId));
        if (byApp) return byApp;
      }
      if (teid != null && String(teid).trim() !== "") {
        const byId = trainerPhotoLookup.byOrgUserId.get(String(teid));
        if (byId) return byId;
      }
      if (nameKey && nameKey !== "trainer") {
        const byName = trainerPhotoLookup.byName.get(nameKey);
        if (byName) return byName;

        const parts = nameKey.split(" ").filter(Boolean);
        if (parts.length >= 2) {
          const firstLast = `${parts[0]} ${parts[parts.length - 1]}`;
          const fuzzy = trainerPhotoLookup.byName.get(firstLast);
          if (fuzzy) return fuzzy;
        }
      }
      return "";
    },
    [trainerPhotoLookup, trainerPersonPhotoIndex, normalizePersonNameKey]
  );

  // Trainer dropdown label: only trainer name (no batch/module).
  const batchTrainerLabel = (item) => resolveTrainerName(item);

  const formatDateDMY = (iso) => {
    if (!iso) return "-";
    const s = String(iso).slice(0, 10);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return s;
    return `${m[3]}-${m[2]}-${m[1]}`;
  };

  const latestRecordForRow = (row) =>
    row?.latestBatchClassRecord ?? row?.latest_batch_class_record ?? null;

  const classDateForRow = (row) =>
    latestRecordForRow(row)?.class_date ??
    row?.class_date ??
    null;

  const classStartTimeForRow = (row) =>
    row?.start_time ??
    latestRecordForRow(row)?.start_time ??
    row?.batch?.batch_start_time ??
    row?.batch_start_time ??
    "";

  const classEndTimeForRow = (row) =>
    row?.end_time ??
    latestRecordForRow(row)?.end_time ??
    row?.batch?.batch_end_time ??
    row?.batch_end_time ??
    "";

  const classModeForRow = (row) =>
    latestRecordForRow(row)?.mode ?? row?.mode ?? row?.batch?.batch_mode ?? "—";

  const classTopicForRow = (row) =>
    latestRecordForRow(row)?.topic ?? row?.topic ?? "—";

  const classStatusForRow = (row) =>
    latestRecordForRow(row)?.status ?? row?.class_status ?? "—";

  const classLocationForRow = (row) =>
    latestRecordForRow(row)?.training_space?.space_name ??
    row?.training_space?.space_name ??
    row?.TrainingSpace?.space_name ??
    (latestRecordForRow(row)?.ems_training_space_id
      ? `Training Space #${latestRecordForRow(row).ems_training_space_id}`
      : null) ??
    (row?.ems_training_space_id ? `Training Space #${row.ems_training_space_id}` : null) ??
    "—";

  const classLastRecordForRow = (row) => {
    const latestStartRaw = latestRecordForRow(row)?.start_time ?? "";
    const statusPart = classStatusForRow(row);
    if (!latestStartRaw) return statusPart || "—";
    const datePart = String(latestStartRaw).includes(" ")
      ? String(latestStartRaw).split(" ")[0]
      : String(latestStartRaw).slice(0, 10);
    const formattedDate = formatDateDMY(datePart);
    if (!statusPart || statusPart === "—") return formattedDate;
    return `${formattedDate} ${statusPart}`;
  };

  const BATCH_DAY_ORDER = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const SHORT_BATCH_DAY = {
    monday: "Mon",
    tuesday: "Tue",
    wednesday: "Wed",
    thursday: "Thu",
    friday: "Fri",
    saturday: "Sat",
    sunday: "Sun",
  };

  const shortBatchDay = (day) => {
    const key = String(day || "").trim().toLowerCase();
    if (SHORT_BATCH_DAY[key]) return SHORT_BATCH_DAY[key];
    if (key.length >= 3) {
      return key.slice(0, 3).replace(/^\w/, (c) => c.toUpperCase());
    }
    return day;
  };

  const parseBatchDaysList = (days) => {
    if (days == null) return [];
    if (Array.isArray(days)) {
      return days.map((d) => String(d).trim()).filter(Boolean);
    }
    if (typeof days === "string") {
      const t = days.trim();
      if (!t) return [];
      if (t.startsWith("[")) {
        try {
          const parsed = JSON.parse(t);
          if (Array.isArray(parsed)) {
            return parsed.map((d) => String(d).trim()).filter(Boolean);
          }
        } catch {
          /* fall through */
        }
      }
      return t.split(",").map((d) => d.trim()).filter(Boolean);
    }
    return [];
  };

  /** Monday → Sunday; unknown labels sort last. All days preserved (deduped by weekday). */
  const batchDaySortIndex = (token) => {
    const s = String(token).trim().toLowerCase();
    for (let i = 0; i < BATCH_DAY_ORDER.length; i++) {
      const w = BATCH_DAY_ORDER[i];
      if (s === w) return i;
    }
    for (let i = 0; i < BATCH_DAY_ORDER.length; i++) {
      const w = BATCH_DAY_ORDER[i];
      if (s.startsWith(w.slice(0, 3))) return i;
    }
    return 999;
  };

  const formatBatchDays = (days) => {
    const list = parseBatchDaysList(days);
    if (!list.length) {
      if (days != null && typeof days === "object" && !Array.isArray(days)) {
        try {
          return JSON.stringify(days);
        } catch {
          return "-";
        }
      }
      return "-";
    }
    const tagged = list.map((raw) => ({
      raw,
      ord: batchDaySortIndex(raw),
    }));
    tagged.sort((a, b) => {
      if (a.ord !== b.ord) return a.ord - b.ord;
      return String(a.raw).localeCompare(String(b.raw));
    });
    const seen = new Set();
    const out = [];
    tagged.forEach(({ raw, ord }) => {
      const key = ord === 999 ? `u:${raw}` : `d:${ord}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push(raw);
    });
    return out.map(shortBatchDay).join(", ");
  };

  /** DD-MM-YYYY on one line (hyphens must not trigger breaks from `break-word`). */
  const trainerDetailsDateCellSx = {
    whiteSpace: "nowrap",
    wordBreak: "normal",
    overflowWrap: "normal",
    verticalAlign: "top",
    minWidth: 104,
  };

  /** `h:mm AM` must stay on one line (space before AM/PM must not wrap). */
  const trainerDetailsTimeCellSx = {
    whiteSpace: "nowrap",
    wordBreak: "normal",
    overflowWrap: "normal",
    verticalAlign: "middle",
    minWidth: 96,
  };

  /** One line per cell; table scrolls horizontally instead of wrapping into “next line”. */
  const trainerDetailsSingleLineCellSx = {
    whiteSpace: "nowrap",
    wordBreak: "normal",
    overflowWrap: "normal",
    verticalAlign: "middle",
  };

  const formatTimeAmPm = (value) => {
    if (!value) return "-";
    const s = String(value).trim();
    if (!s) return "-";
    const m24 = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (m24) {
      const hour24 = Number(m24[1]);
      const minutes = m24[2];
      if (Number.isNaN(hour24) || hour24 < 0 || hour24 > 23) return s;
      const ampm = hour24 >= 12 ? "PM" : "AM";
      const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
      return `${hour12}:${minutes}\u00A0${ampm}`;
    }
    const m12 = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
    if (m12) {
      const h = Number(m12[1]);
      const min = m12[2] != null ? m12[2] : "00";
      return `${h}:${min.padStart(2, "0")}\u00A0${m12[3].toUpperCase()}`;
    }
    return s;
  };

  /** For sorting: minutes from midnight. Supports 24h `HH:MM` / `HH:MM:SS` and 12h `h AM/PM`. */
  const minutesSinceMidnight = (value) => {
    if (value == null) return NaN;
    const s = String(value).trim();
    if (!s) return NaN;
    const m24 = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (m24) {
      const h = Number(m24[1]);
      const min = Number(m24[2]);
      if (Number.isNaN(h) || Number.isNaN(min) || h < 0 || h > 23) return NaN;
      return h * 60 + min;
    }
    const m12 = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
    if (m12) {
      let h = Number(m12[1]);
      const min = Number(m12[2] != null ? Number(m12[2]) : 0);
      const mer = m12[3].toUpperCase();
      if (Number.isNaN(h) || Number.isNaN(min)) return NaN;
      if (mer === "AM") {
        if (h === 12) h = 0;
      } else {
        if (h !== 12) h += 12;
      }
      return h * 60 + min;
    }
    return NaN;
  };
  

  const activeBatchClassTrainers = React.useMemo(
    () =>
      (batchClassTrainers || []).filter((row) => {
        const status = String(row?.status || "Active").trim().toLowerCase();
        return status !== "inactive";
      }),
    [batchClassTrainers]
  );

  const classTrainersByClassId = React.useMemo(() => {
    const map = new Map();
    activeBatchClassTrainers.forEach((row) => {
      const classId =
        row?.organization_ems_batch_class_id ??
        row?.batch_class?.organization_ems_batch_class_id ??
        row?.batchClass?.organization_ems_batch_class_id;
      if (classId == null || classId === "") return;
      const key = String(classId);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    });
    return map;
  }, [activeBatchClassTrainers]);

  const selfTrainerEmployeeIds = React.useMemo(() => {
    const ids = new Set();
    activeBatchClassTrainers.forEach((row) => {
      if (!emsRowMatchesLoggedInTrainer(row, userData)) return;
      const teid = row?.trainer_employee_id;
      if (teid != null && teid !== "") ids.add(String(teid));
    });
    return ids;
  }, [activeBatchClassTrainers, userData]);

  const firstSelfTrainerAssignmentRow = React.useMemo(
    () =>
      activeBatchClassTrainers.find((row) =>
        selfTrainerEmployeeIds.has(String(row?.trainer_employee_id ?? ""))
      ) ?? null,
    [activeBatchClassTrainers, selfTrainerEmployeeIds]
  );

  const hasLoggedInTrainerAssignments = Boolean(firstSelfTrainerAssignmentRow);

  const effectiveSelectedBatchTrainerId = hasLoggedInTrainerAssignments
    ? String(firstSelfTrainerAssignmentRow?.trainer_employee_id ?? "")
    : String(selectedBatchTrainerId || "");

  // Dropdown should show each trainer (by trainer_employee_id) once.
  const trainerOptions = React.useMemo(() => {
    const map = new Map();
    const ongoingClassIds = new Set(
      (batchTrainerList || [])
        .map((row) => row?.organization_ems_batch_class_id)
        .filter((id) => id != null && id !== "")
        .map((id) => String(id))
    );
    const source = hasLoggedInTrainerAssignments
      ? activeBatchClassTrainers.filter((row) =>
          emsRowMatchesLoggedInTrainer(row, userData)
        )
      : activeBatchClassTrainers;
    source.forEach((row) => {
      const classId = String(row?.organization_ems_batch_class_id ?? "");
      if (ongoingClassIds.size && classId && !ongoingClassIds.has(classId)) return;
      const teid = row?.trainer_employee_id;
      if (teid == null || teid === "") return;
      const key = String(teid);
      if (!map.has(key)) map.set(key, row);
    });
    return Array.from(map.values());
  }, [
    activeBatchClassTrainers,
    batchTrainerList,
    hasLoggedInTrainerAssignments,
    userData,
  ]);

  // No selection: show all batch-trainer rows from the list API.
  // With selection: filter to that trainer_employee_id only.
  // Sort by batch start time (morning → evening), then trainer name for ties.
  const selectedTrainerAssignments = React.useMemo(() => {
    const all = batchTrainerList || [];
    const classMatchesTrainer = (row, trainerEmployeeId) => {
      const classId = String(row?.organization_ems_batch_class_id ?? "");
      const linked = classTrainersByClassId.get(classId) || [];
      return linked.some(
        (trainer) =>
          String(trainer?.trainer_employee_id ?? "") === String(trainerEmployeeId)
      );
    };
    const rows = hasLoggedInTrainerAssignments
      ? all.filter((row) => {
          const classId = String(row?.organization_ems_batch_class_id ?? "");
          const linked = classTrainersByClassId.get(classId) || [];
          return linked.some((trainer) =>
            selfTrainerEmployeeIds.has(String(trainer?.trainer_employee_id ?? ""))
          );
        })
      : effectiveSelectedBatchTrainerId
        ? all.filter((row) =>
            classMatchesTrainer(row, effectiveSelectedBatchTrainerId)
          )
        : [...all];
    const trainerNameSortKey = (row) =>
      String(resolveTrainerName(row) || "").toLowerCase();

    const startKey = (row) =>
      minutesSinceMidnight(classStartTimeForRow(row));
    return [...rows].sort((a, b) => {
      const ma = startKey(a);
      const mb = startKey(b);
      const aBad = Number.isNaN(ma);
      const bBad = Number.isNaN(mb);
      if (aBad && bBad) {
        return trainerNameSortKey(a).localeCompare(trainerNameSortKey(b));
      }
      if (aBad) return 1;
      if (bBad) return -1;
      if (ma !== mb) return ma - mb;
      return trainerNameSortKey(a).localeCompare(trainerNameSortKey(b));
    });
  }, [
    batchTrainerList,
    classTrainersByClassId,
    effectiveSelectedBatchTrainerId,
    hasLoggedInTrainerAssignments,
    selfTrainerEmployeeIds,
  ]);

  const timingSlotKeyForRow = (row) => {
    const start = String(classStartTimeForRow(row) || "").trim();
    const end = String(classEndTimeForRow(row) || "").trim();
    return `${start}|${end}`;
  };

  /** Group batch-class rows by batch; merge trainers per timing slot; separate lines per slot. */
  const groupedBatchTrainerDisplayRows = React.useMemo(() => {
    const rows = selectedTrainerAssignments || [];
    const batchMap = new Map();

    rows.forEach((row) => {
      const batchId =
        row?.batch?.organization_ems_batch_id ?? row?.organization_ems_batch_id;
      if (batchId == null || batchId === "") return;

      const batchKey = String(batchId);
      if (!batchMap.has(batchKey)) {
        batchMap.set(batchKey, {
          batchId: batchKey,
          batchCode: row?.batch?.batch_code || row?.batch_code || "—",
          trainingProgram:
            row?.batch?.training_program?.training_program_name || "—",
          batchDays: row?.batch?.batch_days,
          sampleRow: row,
          slotMap: new Map(),
        });
      }

      const batchGroup = batchMap.get(batchKey);
      const slotKey = timingSlotKeyForRow(row);
      if (!batchGroup.slotMap.has(slotKey)) {
        batchGroup.slotMap.set(slotKey, {
          slotKey,
          startTimeRaw: classStartTimeForRow(row),
          endTimeRaw: classEndTimeForRow(row),
          startMinutes: minutesSinceMidnight(classStartTimeForRow(row)),
          trainerNamesById: new Map(),
          rows: [],
        });
      }

      const slot = batchGroup.slotMap.get(slotKey);
      slot.rows.push(row);
      const classId = String(row?.organization_ems_batch_class_id ?? "");
      const linkedTrainers = classTrainersByClassId.get(classId) || [];
      linkedTrainers.forEach((trainerRow) => {
        const teid = trainerRow?.trainer_employee_id;
        const tname = resolveTrainerName(trainerRow);
        if (!tname || tname === "Trainer") return;
        const trainerKey =
          teid != null && teid !== "" ? String(teid) : String(tname);
        if (!slot.trainerNamesById.has(trainerKey)) {
          slot.trainerNamesById.set(trainerKey, {
            id: trainerKey,
            name: tname,
            imageUrl: resolveTrainerImageUrl(trainerRow),
          });
          return;
        }
        const existing = slot.trainerNamesById.get(trainerKey);
        if (existing && !existing.imageUrl) {
          const nextUrl = resolveTrainerImageUrl(trainerRow);
          if (nextUrl) {
            slot.trainerNamesById.set(trainerKey, {
              ...existing,
              imageUrl: nextUrl,
            });
          }
        }
      });
    });

    const groups = Array.from(batchMap.values()).map((batchGroup) => {
      const bid = batchGroup.batchId;
      let studentsDisplay = "—";
      if (batchStudentCountByBatchId === null) studentsDisplay = "…";
      else studentsDisplay = batchStudentCountByBatchId[String(bid)] ?? 0;

      const timingLines = Array.from(batchGroup.slotMap.values())
        .map((slot) => {
          const trainerNames = Array.from(slot.trainerNamesById.values()).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
          return {
            slotKey: slot.slotKey,
            startTimeRaw: slot.startTimeRaw,
            endTimeRaw: slot.endTimeRaw,
            startMinutes: slot.startMinutes,
            trainerNames,
            trainerLabel: trainerNames.map((t) => t.name).join(", "),
            studentCount: studentsDisplay,
          };
        })
        .sort((a, b) => {
          const aBad = Number.isNaN(a.startMinutes);
          const bBad = Number.isNaN(b.startMinutes);
          if (aBad && bBad) return 0;
          if (aBad) return 1;
          if (bBad) return -1;
          return a.startMinutes - b.startMinutes;
        });

      const sortStartMinutes = timingLines[0]?.startMinutes ?? NaN;
      return {
        ...batchGroup,
        timingLines,
        sortStartMinutes,
      };
    });

    groups.sort((a, b) => {
      const ma = a.sortStartMinutes;
      const mb = b.sortStartMinutes;
      const aBad = Number.isNaN(ma);
      const bBad = Number.isNaN(mb);
      if (aBad && bBad) {
        return String(a.batchCode).localeCompare(String(b.batchCode));
      }
      if (aBad) return 1;
      if (bBad) return -1;
      if (ma !== mb) return ma - mb;
      return String(a.batchCode).localeCompare(String(b.batchCode));
    });

    return groups;
  }, [
    selectedTrainerAssignments,
    batchStudentCountByBatchId,
    classTrainersByClassId,
    resolveTrainerImageUrl,
  ]);

  /** Students enrolled in the batch (`organization_ems_batch_id`), shared across trainer rows on the same batch. */
  const batchStudentCountForAssignment = (row) => {
    const bid =
      row?.batch?.organization_ems_batch_id ?? row?.organization_ems_batch_id;
    if (bid == null || bid === "") return "—";
    if (batchStudentCountByBatchId === null) return "…";
    return batchStudentCountByBatchId[String(bid)] ?? 0;
  };

  const exportBatchTrainerDetailsToExcel = () => {
    const groups = groupedBatchTrainerDisplayRows || [];
    if (!groups.length) return;

    const dataForExcel = groups.flatMap((group) =>
      (group.timingLines || []).map((line) => ({
        Batch: group.batchCode || "—",
        "Training program": group.trainingProgram || "—",
        Trainer: line.trainerLabel || "—",
        Students: line.studentCount,
        "Start time": formatTimeAmPm(line.startTimeRaw),
        "End time": formatTimeAmPm(line.endTimeRaw),
        "Batch days": formatBatchDays(group.batchDays),
        "Batch name": group.sampleRow?.batch?.batch_name || "—",
        "Start date": formatDateDMY(group.sampleRow?.batch?.start_date),
        "End date": formatDateDMY(group.sampleRow?.batch?.end_date),
      }))
    );

    if (!dataForExcel.length) return;

    const ws = XLSX.utils.json_to_sheet(dataForExcel);
    const headers = Object.keys(dataForExcel[0]);
    ws["!cols"] = headers.map((h) => {
      const headerLen = h.length;
      const maxData = dataForExcel.reduce((max, row) => {
        const v = row[h];
        const len = v == null ? 0 : String(v).length;
        return Math.max(max, len);
      }, headerLen);
      return { wch: Math.min(Math.max(maxData + 2, headerLen + 1), 55) };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Current Batches");

    const stamp = new Date().toISOString().slice(0, 10);
    const suffix = effectiveSelectedBatchTrainerId
      ? `_trainer_${effectiveSelectedBatchTrainerId}`
      : "_all_trainers";
    const filename = `batch_trainer_details${suffix}_${stamp}.xlsx`;

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Fetch lead summary for the dashboard (replaces other widgets)
  React.useEffect(() => {
    if (!organizationId) {
      setLeadsSummary(null);
      setLeadsSummaryLoading(false);
      setLeadsSummaryError(null);
      if (userData && !organizationId) {
        setLeadsSummaryError("organization_id not found for this organization.");
      }
      return;
    }

    let isActive = true;
    const load = async () => {
      try {
        setLeadsSummaryLoading(true);
        const token = localStorage.getItem("token");
        // Exact route you shared:
        // Route::get('/leads-summary', ...) => https://api.erp.nsplprojects.com/api/ems/leads-summary
        const candidateUrls = [
          `${MAIN_URL}/api/ems/leads-summary?organization_id=${organizationId}`,
        ];

        let lastError = null;
        for (const url of candidateUrls) {
          try {
            const res = await fetch(url, {
              method: "GET",
              headers: {
                Accept: "application/json",
                ...getApiHeaders(),
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              mode: "cors",
            });
            if (!res.ok) {
              throw new Error(`HTTP ${res.status} for ${url}`);
            }
            const json = await res.json();
            if (!isActive) return;
            console.log("json is : ", json);
            setLeadsSummary(json?.data || null);
            setLeadsSummaryError(null);
            lastError = null;
            break;
          } catch (e) {
            lastError = e;
          }
        }

        if (isActive && lastError) {
          setLeadsSummary(null);
          setLeadsSummaryError(
            `Lead summary failed. Tried: ${candidateUrls.join(
              " | "
            )}. Last error: ${String(lastError?.message || lastError)}`
          );
        }
      } catch (e) {
        if (!isActive) return;
        setLeadsSummary(null);
        setLeadsSummaryError(String(e?.message || e));
      } finally {
        if (!isActive) return;
        setLeadsSummaryLoading(false);
      }
    };

    load();
    return () => {
      isActive = false;
    };
  }, [organizationId, userData]);

  console.log("dsata is ojoj", internsApiUrl);
  

  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const theme = useTheme();
  const isNarrowChart = useMediaQuery("(max-width: 768px)");

  React.useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme.palette.mode);
  }, [theme.palette.mode]);

  const currentDate = new Date();
  const options = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };
  const formattedDate = currentDate.toLocaleString("en-US", options);

  const TRAINING_CATEGORY_COLORS = React.useMemo(
    () => [
      "#1565c0",
      "#2e7d32",
      "#ef6c00",
      "#c62828",
      "#6a1b9a",
      "#00695c",
      "#0277bd",
      "#5d4037",
      "#455a64",
    ],
    []
  );

  const CHART_PLOT_HEIGHT = 300;

  const trainingChartData = React.useMemo(() => {
    const tpGraph = leadsSummary?.training_program_graph;
    const tpCategories = Array.isArray(tpGraph?.categories)
      ? tpGraph.categories
      : [];
    const tpPrograms = Array.isArray(tpGraph?.programs) ? tpGraph.programs : [];
    const tpProgramIds = Array.isArray(tpGraph?.program_ids) ? tpGraph.program_ids : [];
    const tpValues = Array.isArray(tpGraph?.values) ? tpGraph.values : [];

    if (!tpPrograms.length || !tpValues.length) {
      return { bars: [], maxVal: 1, hasMatrix: false };
    }

    const numCats = tpCategories.length;

    if (trainingGraphCategory === "all") {
      const allBars = tpPrograms.map((programName, j) => {
        const segments = [];
        let total = 0;
        for (let i = 0; i < numCats; i++) {
          const v = Number(tpValues[i]?.[j] ?? 0);
          if (v > 0) {
            segments.push({
              categoryIndex: i,
              label: tpCategories[i] ?? `Category ${i}`,
              value: v,
              color:
                TRAINING_CATEGORY_COLORS[i % TRAINING_CATEGORY_COLORS.length],
            });
          }
          total += v;
        }
        return { programName, programIndex: j, programId: tpProgramIds[j] ?? null, segments, total };
      });
      const bars = allBars.filter((b) => b.total > 0);
      const maxVal = Math.max(
        1,
        ...bars.map((b) => b.total),
        1
      );
      return { bars, maxVal, hasMatrix: true };
    }

    const ci = trainingGraphCategory;
    if (typeof ci !== "number" || ci < 0 || ci >= numCats) {
      return { bars: [], maxVal: 1, hasMatrix: true };
    }

    const row = tpValues[ci] || [];
    const bars = tpPrograms
      .map((programName, j) => {
        const v = Number(row[j] ?? 0);
        return {
          programName,
          programIndex: j,
          programId: tpProgramIds[j] ?? null,
          segments:
            v > 0
              ? [
                  {
                    categoryIndex: ci,
                    label: tpCategories[ci],
                    value: v,
                    color:
                      TRAINING_CATEGORY_COLORS[
                        ci % TRAINING_CATEGORY_COLORS.length
                      ],
                  },
                ]
              : [],
          total: v,
        };
      })
      .filter((b) => b.total > 0);

    const maxVal = Math.max(1, ...bars.map((b) => b.total));
    return { bars, maxVal, hasMatrix: true };
  }, [leadsSummary, trainingGraphCategory, TRAINING_CATEGORY_COLORS]);

  const formatMonthChartLabel = (ym) => {
    const match = /^(\d{4})-(\d{1,2})$/.exec(String(ym ?? "").trim());
    if (!match) return String(ym ?? "");
    const y = Number(match[1]);
    const m = Number(match[2]);
    if (!y || m < 1 || m > 12) return String(ym);
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sept", "Oct", "Nov", "Dec",
    ];
    return `${monthNames[m - 1]} ${y}`;
  };

  const formatMonthChartTick = (ym) => {
    const match = /^(\d{4})-(\d{1,2})$/.exec(String(ym ?? "").trim());
    if (!match) return String(ym ?? "");
    const y = Number(match[1]);
    const m = Number(match[2]);
    if (!y || m < 1 || m > 12) return String(ym);
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    return monthNames[m - 1];
  };

  const normalizeMonthKey = (ym) => {
    const match = /^(\d{4})-(\d{1,2})$/.exec(String(ym ?? "").trim());
    if (!match) return String(ym ?? "").trim();
    return `${match[1]}-${String(Number(match[2])).padStart(2, "0")}`;
  };

  const buildExpectedMonthlyRows = (period, rows) => {
    if (!period?.from || !period?.to) return rows;
    const parse = (ym) => {
      const match = /^(\d{4})-(\d{1,2})$/.exec(String(ym ?? "").trim());
      if (!match) return null;
      return { y: Number(match[1]), m: Number(match[2]) };
    };
    const start = parse(period.from);
    const end = parse(period.to);
    if (!start || !end) return rows;

    const slots = [];
    let y = start.y;
    let m = start.m;
    while (y < end.y || (y === end.y && m <= end.m)) {
      slots.push(`${y}-${String(m).padStart(2, "0")}`);
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
    }

    const byMonth = {};
    rows.forEach((row) => {
      byMonth[normalizeMonthKey(row.month)] = row;
    });

    return slots.map((month) => ({
      month,
      ...(byMonth[month] || {}),
    }));
  };

  const feesCollectedMonthlyChart = React.useMemo(() => {
    const graph = leadsSummary?.fees_collected_monthly_graph;
    const rawRows = Array.isArray(graph?.data) ? graph.data : [];
    const rows = buildExpectedMonthlyRows(graph?.period, rawRows);
    const bars = rows.map((row, index) => {
      const month = String(row.month ?? "").trim();
      return {
        slot: index,
        month,
        monthTick: formatMonthChartTick(month),
        amount: Number(row.amount_inr ?? 0),
        paymentCount: Number(row.payment_count ?? 0),
      };
    });
    const maxVal = Math.max(1, ...bars.map((b) => b.amount));
    const total = bars.reduce((sum, b) => sum + b.amount, 0);
    return {
      bars,
      maxVal,
      total,
      period: graph?.period ?? null,
    };
  }, [leadsSummary]);

  const admissionsMonthlyChart = React.useMemo(() => {
    const graph = leadsSummary?.admissions_monthly_graph;
    const rawRows = Array.isArray(graph?.data) ? graph.data : [];
    const rows = buildExpectedMonthlyRows(graph?.period, rawRows);
    const points = rows.map((row, index) => {
      const month = String(row.month ?? "").trim();
      return {
        slot: index,
        month,
        monthTick: formatMonthChartTick(month),
        count: Number(row.count ?? 0),
      };
    });
    const maxVal = Math.max(1, ...points.map((p) => p.count));
    const total = points.reduce((sum, p) => sum + p.count, 0);
    return {
      points,
      maxVal,
      total,
      period: graph?.period ?? null,
    };
  }, [leadsSummary]);

  const formatMonthlyChartPeriod = (period) => {
    if (!period?.from || !period?.to) return "Last 12 months";
    return `${formatMonthChartLabel(period.from)} – ${formatMonthChartLabel(period.to)}`;
  };

  const formatInrAxis = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "0";
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
    if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
    return `₹${n}`;
  };

  const formatFeesChartYAxis = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n === 0) return "₹0";
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
    if (n >= 100000) {
      const lakhs = n / 100000;
      const label =
        Math.abs(lakhs - Math.round(lakhs)) < 1e-9
          ? String(Math.round(lakhs))
          : lakhs.toFixed(1);
      return `₹${label}L`;
    }
    if (n >= 1000) return `₹${Math.round(n / 1000)}K`;
    return `₹${n}`;
  };

  const feesChartYAxisConfig = React.useMemo(() => {
    const step = 50000;
    const maxAmount = feesCollectedMonthlyChart.maxVal;
    const max = Math.max(step, Math.ceil(maxAmount / step) * step);
    const ticks = [];
    for (let v = 0; v <= max; v += step) {
      ticks.push(v);
    }
    return { max, ticks };
  }, [feesCollectedMonthlyChart.maxVal]);

  const admissionsChartYAxisConfig = React.useMemo(() => {
    const step = 10;
    const maxCount = admissionsMonthlyChart.maxVal;
    const max = Math.max(step, Math.ceil(maxCount / step) * step);
    const ticks = [];
    for (let v = 0; v <= max; v += step) {
      ticks.push(v);
    }
    return { max, ticks };
  }, [admissionsMonthlyChart.maxVal]);

  const calculateWidgetValues = React.useMemo(() => {
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    let newJoiningCount = 0;
    let birthdayCount = 0;
    let anniversaryCount = 0;

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    employees.forEach((employee) => {
      // Process Date of Joining
      if (employee.Date_of_Joining) {
        const [dayStr, monthStr, yearStr] = employee.Date_of_Joining.split("-");
        const joinMonth = monthNames.indexOf(monthStr);
        const joinYear = Number.parseInt(yearStr);

        if (joinMonth === currentMonth) {
          if (joinYear === currentYear) {
            newJoiningCount++;
          } else if (joinYear < currentYear) {
            anniversaryCount++;
          }
        }
      }

      // Process Date of Birth (handles ISO format and "dd-MMM-yyyy" format)
      if (employee.Date_of_Birth) {
        let birthDateObj;
        if (employee.Date_of_Birth.includes("T")) {
          // ISO format
          birthDateObj = new Date(employee.Date_of_Birth);
        } else {
          // "dd-MMM-yyyy" format
          const birthParts = employee.Date_of_Birth.split("-");
          if (birthParts.length === 3) {
            const monthIndex = monthNames.indexOf(birthParts[1]);
            if (monthIndex !== -1) {
              birthDateObj = new Date(
                Number(birthParts[2]),
                monthIndex,
                Number(birthParts[0])
              );
            }
          }
        }
        if (birthDateObj && birthDateObj.getMonth() === currentMonth) {
          birthdayCount++;
        }
      }
    });

    return {
      newJoining: newJoiningCount,
      birthday: birthdayCount,
      anniversary: anniversaryCount,
    };
  }, [employees, currentDate]);

  // NEW: Calculate intern widget values
  const calculateInternWidgetValues = React.useMemo(() => {
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    let newJoiningCount = 0;
    let birthdayCount = 0;
    let anniversaryCount = 0;

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    interns.forEach((intern) => {
      // Process Date of Joining
      if (intern.Date_of_Joining) {
        const [dayStr, monthStr, yearStr] = intern.Date_of_Joining.split("-");
        const joinMonth = monthNames.indexOf(monthStr);
        const joinYear = Number.parseInt(yearStr);

        if (joinMonth === currentMonth) {
          if (joinYear === currentYear) {
            newJoiningCount++;
          } else if (joinYear < currentYear) {
            anniversaryCount++;
          }
        }
      }

      // Process Date of Birth (handles ISO format and "dd-MMM-yyyy" format)
      if (intern.Date_of_Birth) {
        let birthDateObj;
        if (intern.Date_of_Birth.includes("T")) {
          // ISO format
          birthDateObj = new Date(intern.Date_of_Birth);
        } else {
          // "dd-MMM-yyyy" format
          const birthParts = intern.Date_of_Birth.split("-");
          if (birthParts.length === 3) {
            const monthIndex = monthNames.indexOf(birthParts[1]);
            if (monthIndex !== -1) {
              birthDateObj = new Date(
                Number(birthParts[2]),
                monthIndex,
                Number(birthParts[0])
              );
            }
          }
        }
        if (birthDateObj && birthDateObj.getMonth() === currentMonth) {
          birthdayCount++;
        }
      }
    });

    return {
      newJoining: newJoiningCount,
      birthday: birthdayCount,
      anniversary: anniversaryCount,
    };
  }, [interns, currentDate]);

  // Calculate freelancer widget values
  const calculateFreelancerWidgetValues = React.useMemo(() => {
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    let newJoiningCount = 0;
    let birthdayCount = 0;
    let anniversaryCount = 0;

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    freelancers.forEach((freelancer) => {
      if (freelancer.Date_of_Joining) {
        const [dayStr, monthStr, yearStr] =
          freelancer.Date_of_Joining.split("-");
        const joinMonth = monthNames.indexOf(monthStr);
        const joinYear = Number.parseInt(yearStr);

        if (joinMonth === currentMonth) {
          if (joinYear === currentYear) {
            newJoiningCount++;
          } else if (joinYear < currentYear) {
            anniversaryCount++;
          }
        }
      }

      if (freelancer.Date_of_Birth) {
        let birthDateObj;
        if (freelancer.Date_of_Birth.includes("T")) {
          birthDateObj = new Date(freelancer.Date_of_Birth);
        } else {
          const birthParts = freelancer.Date_of_Birth.split("-");
          if (birthParts.length === 3) {
            const monthIndex = monthNames.indexOf(birthParts[1]);
            if (monthIndex !== -1) {
              birthDateObj = new Date(
                Number(birthParts[2]),
                monthIndex,
                Number(birthParts[0])
              );
            }
          }
        }
        if (birthDateObj && birthDateObj.getMonth() === currentMonth) {
          birthdayCount++;
        }
      }
    });

    return {
      newJoining: newJoiningCount,
      birthday: birthdayCount,
      anniversary: anniversaryCount,
    };
  }, [freelancers, currentDate]);

  const attendanceStats = React.useMemo(() => {
    let present = 0;
    let absent = 0;
    let late = 0;

    const normalizeBool = (val) => {
      if (typeof val === "boolean") return val;
      if (typeof val === "number") return val > 0;
      if (typeof val === "string") {
        const s = val.trim().toLowerCase();
        if (
          [
            "true",
            "1",
            "yes",
            "y",
            "present",
            "checked-in",
            "clocked-in",
            "in",
          ].includes(s)
        )
          return true;
        if (
          ["false", "0", "no", "n", "absent", "not-present", "out"].includes(s)
        )
          return false;
      }
      return null;
    };

    const isNonEmptyTime = (val) => {
      if (val == null) return false;
      if (typeof val === "number") return val > 0;
      if (typeof val === "string") {
        const s = val.trim().toLowerCase();
        if (!s) return false;
        if (["null", "undefined", "na", "n/a"].includes(s)) return false;
        if (
          ["00:00", "00:00:00", "0", "1970-01-01", "invalid date"].includes(s)
        )
          return false;
        return true;
      }
      if (val instanceof Date && !isNaN(val.getTime())) return true;
      return false;
    };

    employees.forEach((emp) => {
      const today =
        emp?.attendance_today ||
        emp?.Attendance?.[0] ||
        emp?.attendance?.[0] ||
        null;

      const clockInCandidates = [
        today?.clock_in,
        today?.ClockIn,
        today?.clockIn,
        today?.clockin,
        emp?.clock_in,
        emp?.ClockIn,
        emp?.clockIn,
        emp?.clockin,
      ];
      const clockOutCandidates = [
        today?.clock_out,
        today?.ClockOut,
        today?.clockOut,
        today?.clockout,
        emp?.clock_out,
        emp?.ClockOut,
        emp?.clockOut,
        emp?.clockout,
      ];

      let clockInFlag = null;
      for (const c of clockInCandidates) {
        const n = normalizeBool(c);
        if (n !== null) {
          clockInFlag = n;
          break;
        }
      }

      let clockOutFlag = null;
      for (const c of clockOutCandidates) {
        const n = normalizeBool(c);
        if (n !== null) {
          clockOutFlag = n;
          break;
        }
      }

      const hasCheckInTimestamp = [
        today?.clock_in,
        today?.ClockIn,
        today?.clockIn,
        today?.clockin,
        today?.check_in,
        today?.CheckIn,
        today?.checkIn,
        today?.time_in,
        today?.TimeIn,
        today?.in_time,
        today?.first_check_in,
        today?.firstCheckIn,
      ].some(isNonEmptyTime);

      let counted = false;

      if (clockInFlag === false || clockOutFlag === true) {
        present += 1;
        counted = true;
      } else if (clockInFlag === true && !hasCheckInTimestamp) {
        absent += 1;
        counted = true;
      } else if (hasCheckInTimestamp) {
        present += 1;
        counted = true;
      }

      if (!counted) {
        const rawStatus =
          today?.status ||
          today?.Status ||
          emp?.AttendanceStatus ||
          emp?.today_status ||
          emp?.status_today ||
          "";
        const status =
          typeof rawStatus === "string" ? rawStatus.toLowerCase() : "";
        if (
          status.includes("present") ||
          status === "in" ||
          status === "checked-in"
        )
          present += 1;
        else if (
          status.includes("absent") ||
          status === "out" ||
          status === "checked-out"
        )
          absent += 1;
      }

      if (
        normalizeBool(today?.late) === true ||
        normalizeBool(emp?.late_today) === true
      ) {
        late += 1;
      } else {
        const statusLate = String(
          today?.status || today?.Status || ""
        ).toLowerCase();
        if (statusLate.includes("late")) late += 1;
      }
    });

    return { present, absent, late };
  }, [employees, currentDate]);

  // React.useEffect(() => {
  //   const orgId = org?.organization_id || 5;
  //   if (!orgId) return;

  //   const leavesUrl = `${MAIN_URL}/api/organizations/${orgId}/employee-leaves-all`;
  //   const controller = new AbortController();

  //   const fetchLeaves = async () => {
  //     try {
  //       const response = await fetch(leavesUrl, {
  //         method: "GET",
  //         headers: {
  //           Accept: "application/json",
  //         },
  //         signal: controller.signal,
  //       });
  //       if (!response.ok)
  //         throw new Error(`HTTP error! status: ${response.status}`);

  //       const json = await response.json();
  //       let leaves = Array.isArray(json)
  //         ? json
  //         : json.data || json.leaves || json.results || [];

  //       if (
  //         Array.isArray(leaves) &&
  //         leaves.length > 0 &&
  //         Array.isArray(leaves[0])
  //       ) {
  //         leaves = leaves[0];
  //       }

  //       const y = currentDate.getFullYear();
  //       const m = currentDate.getMonth();
  //       const d = currentDate.getDate();
  //       const isSameDay = (dateObj) =>
  //         dateObj &&
  //         dateObj.getFullYear() === y &&
  //         dateObj.getMonth() === m &&
  //         dateObj.getDate() === d;

  //       const count = (leaves || []).reduce((acc, item) => {
  //         const startStr =
  //           item?.leave_start_date ||
  //           item?.start_date ||
  //           item?.from_date ||
  //           item?.fromDate;
  //         if (!startStr) return acc;
  //         const start = new Date(startStr);
  //         return isSameDay(start) ? acc + 1 : acc;
  //       }, 0);

  //       setOnLeaveTodayApiCount(count);
  //     } catch (e) {
  //       setOnLeaveTodayApiCount(0);
  //     }
  //   };

  //   fetchLeaves();
  //   return () => controller.abort();
  // }, [org?.organization_id, currentDate]);

  const leavesStats = React.useMemo(() => {
    let onLeaveToday = 0;
    let upcomingLeaves = 0;
    let pending = 0;

    const now = currentDate;
    const in7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    employees.forEach((emp) => {
      const todayLeave =
        emp.today_leave ||
        emp.LeaveToday ||
        emp.leave_today ||
        emp.is_on_leave_today ||
        false;
      if (todayLeave) onLeaveToday += 1;

      const pendingReq = Number(
        emp.pending_leave_requests || emp.pendingLeaves || 0
      );
      if (!Number.isNaN(pendingReq)) pending += pendingReq;

      const upcomingArr =
        emp.upcoming_leaves || emp.UpcomingLeaves || emp.upcomingLeaves || [];

      if (Array.isArray(upcomingArr)) {
        upcomingLeaves += upcomingArr.filter((l) => {
          const startStr = l?.start_date || l?.startDate;
          if (!startStr) return false;
          const start = new Date(startStr);
          return start >= now && start <= in7Days;
        }).length;
      }
    });

    const finalOnLeaveToday =
      typeof onLeaveTodayApiCount === "number"
        ? onLeaveTodayApiCount
        : onLeaveToday;

    return { onLeaveToday: finalOnLeaveToday, upcomingLeaves, pending };
  }, [employees, currentDate, onLeaveTodayApiCount]);

  const fetchEmployees = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!apiUrl) {
        setEmployees([]);
        return;
      }

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(() => {
            const token = localStorage.getItem("token");
            return token ? { Authorization: `Bearer ${token}` } : {};
          })(),
        },
        mode: "cors",
      });

      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }

      const data = await response.json();
      let employeesData = Array.isArray(data)
        ? data
        : data.employees || data.data || [];

      if (
        Array.isArray(employeesData) &&
        employeesData.length > 0 &&
        Array.isArray(employeesData[0])
      ) {
        employeesData = employeesData[0];
      }

      setEmployees(employeesData);
    } catch (err) {
      let errorMessage = "Failed to fetch employee data";
      if (
        err?.name === "TypeError" &&
        String(err?.message || "").includes("Failed to fetch")
      ) {
        errorMessage =
          "Network error: Unable to connect to the API. This might be due to CORS restrictions or network connectivity issues.";
      } else if (String(err?.message || "").includes("HTTP error")) {
        errorMessage = `API Error: ${err.message}`;
      } else if (err?.message) {
        errorMessage = `Error: ${err.message}`;
      }
      setError(errorMessage);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  // Fetch freelancers function
  const fetchFreelancers = React.useCallback(async () => {
    try {
      if (!freelancersApiUrl) {
        setFreelancers([]);
        return;
      }

      const response = await fetch(freelancersApiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        mode: "cors",
      });

      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }

      const data = await response.json();
      let freelancersData = Array.isArray(data)
        ? data
        : data.freelancers || data.data || [];

      if (
        Array.isArray(freelancersData) &&
        freelancersData.length > 0 &&
        Array.isArray(freelancersData[0])
      ) {
        freelancersData = freelancersData[0];
      }

      setFreelancers(freelancersData);
    } catch (err) {
      console.error("Failed to fetch freelancer data:", err);
      setFreelancers([]);
    }
  }, [freelancersApiUrl]);

  React.useEffect(() => {
    fetchFreelancers();
  }, [fetchFreelancers]);

  // NEW: Fetch interns function
  const fetchInterns = React.useCallback(async () => {
    try {
      if (!internsApiUrl) {
        setInterns([]);
        return;
      }

      const response = await fetch(internsApiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        mode: "cors",
      });

      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }

      const data = await response.json();
      let internsData = Array.isArray(data)
        ? data
        : data.interns || data.data || [];

      if (
        Array.isArray(internsData) &&
        internsData.length > 0 &&
        Array.isArray(internsData[0])
      ) {
        internsData = internsData[0];
      }

      setInterns(internsData);
    } catch (err) {
      console.error("Failed to fetch intern data:", err);
      setInterns([]);
    }
  }, [internsApiUrl]);



  React.useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  React.useEffect(() => {
    fetchInterns();
  }, [fetchInterns]);

  const handleMenuItemClick = () => {};

  const genderCounts = employees.reduce((acc, emp) => {
    const gender = emp.gender || emp.Gender || emp.sex || emp.Sex || "Unknown";
    acc[gender] = (acc[gender] || 0) + 1;
    return acc;
  }, {});

  const genderData = [
    {
      id: 0,
      value:
        genderCounts["Male"] || genderCounts["male"] || genderCounts["M"] || 0,
      label: "Male",
      color: "#3B82F6",
    },
    {
      id: 1,
      value:
        genderCounts["Female"] ||
        genderCounts["female"] ||
        genderCounts["F"] ||
        0,
      label: "Female",
      color: "#EC4899",
    },
    {
      id: 2,
      value: Object.keys(genderCounts).reduce((otherCount, key) => {
        const normalizedKey = key.toLowerCase();
        if (
          normalizedKey !== "male" &&
          normalizedKey !== "female" &&
          normalizedKey !== "m" &&
          normalizedKey !== "f"
        ) {
          return otherCount + genderCounts[key];
        }
        return otherCount;
      }, 0),
      label: "Other",
      color: "#10B981",
    },
  ];

  const departmentCounts = employees.reduce((acc, emp) => {
    let departmentName = "Unknown";

    if (
      emp.Department &&
      Array.isArray(emp.Department) &&
      emp.Department.length > 0
    ) {
      const deptObj = emp.Department[0];
      departmentName =
        deptObj?.department?.department_short_name ||
        deptObj?.department?.name ||
        deptObj?.department?.Department ||
        deptObj?.department?.department_name ||
        deptObj?.name ||
        deptObj?.Department;
    } else if (emp.department) {
      departmentName =
        emp.department.department_short_name ||
        emp.department.name ||
        emp.department.Department ||
        emp.department.department_name;
    } else if (emp.department_name) {
      departmentName = emp.department_name;
    } else if (typeof emp.Department === "string") {
      departmentName = emp.Department;
    }

    if (!departmentName || departmentName === "") {
      departmentName = "Unknown";
    }

    const getDepartmentAbbreviation = (name) => {
      let cleanName = name.toLowerCase().trim();
      cleanName = cleanName
        .replace(/\s*(department|dept|division|div)\s*$/i, "")
        .trim();

      const abbreviationMap = {
        "human resources": "HR",
        hr: "HR",
        finance: "FIN",
        fin: "FIN",
        "information technology": "IT",
        it: "IT",
        tech: "IT",
        technology: "IT",
        marketing: "MKT",
        mkt: "MKT",
        sales: "SALES",
        operations: "OPS",
        ops: "OPS",
        "research and development": "R&D",
        "r&d": "R&D",
        research: "R&D",
        development: "R&D",
        "customer service": "CS",
        cs: "CS",
        support: "CS",
        legal: "LEGAL",
        administration: "ADMIN",
        admin: "ADMIN",
      };

      return abbreviationMap[cleanName] || name;
    };

    departmentName = getDepartmentAbbreviation(departmentName);

    acc[departmentName] = (acc[departmentName] || 0) + 1;
    return acc;
  }, {});

  const departmentData = Object.entries(departmentCounts).map(
    ([name, value]) => ({ name, value })
  );

  const employmentTypeCounts = employees.reduce((acc, emp) => {
    let employmentTypeName = "Unknown";

    if (emp.employment_type) {
      employmentTypeName = emp.employment_type;
    } else if (
      emp.EmploymentType &&
      Array.isArray(emp.EmploymentType) &&
      emp.EmploymentType.length > 0
    ) {
      const empTypeObj = emp.EmploymentType[0];
      employmentTypeName =
        empTypeObj?.employment_type?.name ||
        empTypeObj?.employment_type?.EmploymentType ||
        empTypeObj?.employment_type?.employment_type_name ||
        empTypeObj?.name ||
        empTypeObj?.EmploymentType;
    } else if (emp.employment_type) {
      employmentTypeName =
        emp.employment_type.name ||
        emp.employment_type.EmploymentType ||
        emp.employment_type.employment_type_name;
    } else if (emp.employment_type_name) {
      employmentTypeName = emp.employment_type_name;
    } else if (typeof emp.EmploymentType === "string") {
      employmentTypeName = emp.EmploymentType;
    }

    if (!employmentTypeName || employmentTypeName === "") {
      employmentTypeName = "Unknown";
    }

    if (employmentTypeName && employmentTypeName !== "Unknown") {
      employmentTypeName = employmentTypeName
        .replace(/\btype\b/gi, "")
        .trim()
        .toUpperCase();
    }

    acc[employmentTypeName] = (acc[employmentTypeName] || 0) + 1;
    return acc;
  }, {});

  const employmentTypeData = Object.entries(employmentTypeCounts).map(
    ([name, value]) => ({ name, value })
  );

  const getShortDesignationName = (designation) => {
    const abbreviationMap = {
      "chief executive officer": "CEO",
      "chief technology officer": "CTO",
      "chief financial officer": "CFO",
      "chief operating officer": "COO",
      "chief marketing officer": "CMO",
      "chief human resources officer": "CHRO",
      "general manager": "GM",
      "project manager": "PM",
      "product manager": "PM",
      "program manager": "PGM",
      "operations manager": "OM",
      "marketing manager": "MM",
      "sales manager": "SM",
      "finance manager": "FinM",
      "hr manager": "HRM",
      "human resources manager": "HRM",
      "security manager": "SM",
      "quality manager": "QM",
      "business development manager": "BDM",
      "software engineer": "SWE",
      "senior software engineer": "SrSE",
      "junior software engineer": "JrSE",
      "software developer": "Dev",
      "senior software developer": "SrDev",
      "junior software developer": "JrDev",
      "full stack developer": "FullStack",
      "frontend developer": "Frontend",
      "backend developer": "Backend",
      "mobile developer": "MobileDev",
      "web developer": "WebDev",
      "devops engineer": "DevOps",
      "system administrator": "SysAdmin",
      "database administrator": "DBA",
      "network administrator": "NetAdmin",
      "quality assurance": "QA",
      "quality assurance engineer": "QA Eng",
      "test engineer": "Test Eng",
      "automation engineer": "Auto Eng",
      "business analyst": "BA",
      "senior business analyst": "Sr BA",
      "data analyst": "DA",
      "senior data analyst": "Sr DA",
      "data scientist": "DS",
      "business intelligence analyst": "BI Analyst",
      "financial analyst": "Fin Analyst",
      "market research analyst": "MR Analyst",
      "sales executive": "Sales Exec",
      "senior sales executive": "Sr Sales",
      "sales representative": "Sales Rep",
      "account manager": "Acct Mgr",
      "account executive": "Acct Exec",
      "marketing executive": "Mkt Exec",
      "digital marketing specialist": "Digital Mkt",
      "content marketing specialist": "Content Mkt",
      "social media manager": "SM Mgr",
      "human resource executive": "HRE",
      "hr executive": "HRE",
      "human resource specialist": "HR Spec",
      "hr specialist": "HR Spec",
      recruiter: "Recruiter",
      "talent acquisition specialist": "TA Spec",
      "administrative assistant": "AdminAsst",
      "executive assistant": "ExecAsst",
      "office manager": "Office Mgr",
      accountant: "Accountant",
      "senior accountant": "Sr Accountant",
      "junior accountant": "Jr Accountant",
      "accounts payable": "AP",
      "accounts receivable": "AR",
      "financial controller": "Controller",
      "finance executive": "FIE",
      "finance head": "FIH",
      "customer service representative": "CSR",
      "customer support": "Support",
      "customer success manager": "CS Mgr",
      "technical support": "Tech Support",
      "help desk": "Help Desk",
      "ui designer": "UI Designer",
      "ux designer": "UX Designer",
      "ui/ux designer": "UI/UX",
      "graphic designer": "GFX Designer",
      "web designer": "Web Designer",
      "product designer": "Product Des",
      "operations executive": "Ops Exec",
      "logistics coordinator": "Logistics",
      "supply chain manager": "SC Mgr",
      "warehouse manager": "WH Mgr",
      "production manager": "Prod Mgr",
      consultant: "Consultant",
      "senior consultant": "Sr Consultant",
      "strategy consultant": "Strategy Con",
      "business consultant": "Biz Consultant",
      "legal counsel": "Legal",
      "compliance officer": "Compliance",
      "legal advisor": "Legal Advisor",
    };

    let cleanDesignation = designation.toLowerCase().trim();

    const suffixesToRemove = [
      " specialist",
      " executive",
      " representative",
      " coordinator",
      " associate",
    ];
    suffixesToRemove.forEach((suffix) => {
      if (
        cleanDesignation.endsWith(suffix) &&
        !abbreviationMap[cleanDesignation]
      ) {
        const withoutSuffix = cleanDesignation.replace(suffix, "").trim();
        if (abbreviationMap[withoutSuffix]) {
          cleanDesignation = withoutSuffix;
        }
      }
    });

    if (abbreviationMap[cleanDesignation]) {
      return abbreviationMap[cleanDesignation];
    }

    for (const [fullName, abbrev] of Object.entries(abbreviationMap)) {
      if (
        fullName.includes(cleanDesignation) ||
        cleanDesignation.includes(fullName.split(" ")[0])
      ) {
        return abbrev;
      }
    }

    const formatted = designation
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    return formatted.length > 12
      ? formatted.substring(0, 12) + "..."
      : formatted;
  };

  const designationCounts = employees.reduce((acc, emp) => {
    const designationName = emp.Designation || "Unknown";
    const shortName = getShortDesignationName(designationName);
    acc[shortName] = (acc[shortName] || 0) + 1;
    return acc;
  }, {});

  const designationData = Object.entries(designationCounts).map(
    ([name, value]) => ({ name, value })
  );

  const ageGroupCounts = employees.reduce((acc, emp) => {
    const age = emp.Age;
    if (age >= 20 && age <= 25) acc["20-25"] = (acc["20-25"] || 0) + 1;
    else if (age >= 26 && age <= 30) acc["26-30"] = (acc["26-30"] || 0) + 1;
    else if (age >= 31 && age <= 35) acc["31-35"] = (acc["31-35"] || 0) + 1;
    else if (age >= 36 && age <= 40) acc["36-40"] = (acc["36-40"] || 0) + 1;
    else if (age > 40) acc["40+"] = (acc["40+"] || 0) + 1;
    return acc;
  }, {});

  const ageGroupData = [
    {
      id: "age20-25",
      value: ageGroupCounts["20-25"] || 0,
      label: "20-25",
      color: "#EF4444",
    },
    {
      id: "age26-30",
      value: ageGroupCounts["26-30"] || 0,
      label: "26-30",
      color: "#F59E0B",
    },
    {
      id: "age31-35",
      value: ageGroupCounts["31-35"] || 0,
      label: "31-35",
      color: "#10B981",
    },
    {
      id: "age36-40",
      value: ageGroupCounts["36-40"] || 0,
      label: "36-40",
      color: "#3B82F6",
    },
    {
      id: "age40+",
      value: ageGroupCounts["40+"] || 0,
      label: "40+",
      color: "#8B5CF6",
    },
  ].filter((item) => item.value > 0);

  const experienceCounts = employees.reduce((acc, emp) => {
    if (emp.Date_of_Joining) {
      try {
        const joiningDate = new Date(emp.Date_of_Joining);
        if (!isNaN(joiningDate.getTime())) {
          const yearsOfExperience =
            currentDate.getFullYear() - joiningDate.getFullYear();

          if (yearsOfExperience >= 0 && yearsOfExperience <= 2)
            acc["0-2 years"] = (acc["0-2 years"] || 0) + 1;
          else if (yearsOfExperience >= 3 && yearsOfExperience <= 5)
            acc["3-5 years"] = (acc["3-5 years"] || 0) + 1;
          else if (yearsOfExperience >= 6 && yearsOfExperience <= 10)
            acc["6-10 years"] = (acc["6-10 years"] || 0) + 1;
          else if (yearsOfExperience > 10)
            acc["10+ years"] = (acc["10+ years"] || 0) + 1;
        }
      } catch (e) {
        console.error(
          `Error processing Date_of_Joining for employee: ${emp.id || emp.name || "Unknown ID"}.`,
          e
        );
      }
    }
    return acc;
  }, {});

  const experienceData = [
    { name: "0-2 years", value: experienceCounts["0-2 years"] || 0 },
    { name: "3-5 years", value: experienceCounts["3-5 years"] || 0 },
    { name: "6-10 years", value: experienceCounts["6-10 years"] || 0 },
    { name: "10+ years", value: experienceCounts["10+ years"] || 0 },
  ];

  const organizationUnitCounts = employees.reduce((acc, emp) => {
    const orgUnitName =
      emp.department_location?.[0]?.department?.department_short_name ||
      emp.department_location?.[0]?.department?.department_name ||
      emp.department?.department_short_name ||
      emp.department?.department_name ||
      emp.department_name ||
      emp.department ||
      "No Department";

    acc[orgUnitName] = (acc[orgUnitName] || 0) + 1;
    return acc;
  }, {});

  const organizationData = Object.entries(organizationUnitCounts).map(
    ([name, value]) => ({ name, value })
  );

  const allChartConfigs = [
    {
      id: "gender",
      title: "Distribution by Gender",
      icon: null,
      type: "pie",
      data: genderData,
      color: "#3B82F6",
      group: "Demographics",
    },
    {
      id: "ageGroup",
      title: "Distribution by Age Group",
      icon: null,
      type: "pie",
      data: ageGroupData,
      color: "#EF4444",
      group: "Demographics",
    },
    (() => {
      const sortedDepartmentData = departmentData.sort(
        (a, b) => b.value - a.value
      );
      return {
        id: "department",
        title: "Distribution by Department",
        icon: null,
        type: "bar",
        data: sortedDepartmentData,
        xAxisData: sortedDepartmentData.map((d) => d.name),
        seriesData: [
          {
            data: sortedDepartmentData.map((d) => d.value),
            label: "Employees",
            color: "#10B981",
          },
        ],
        color: "#10B981",
        group: "Employment Details",
      };
    })(),
    (() => {
      const sortedEmploymentTypeData = employmentTypeData.sort(
        (a, b) => b.value - a.value
      );
      return {
        id: "employmentType",
        title: "Distribution by Employment Type",
        icon: null,
        type: "bar",
        data: sortedEmploymentTypeData,
        xAxisData: sortedEmploymentTypeData.map((d) => d.name),
        seriesData: [
          {
            data: sortedEmploymentTypeData.map((d) => d.value),
            label: "Employees",
            color: "#F97316",
          },
        ],
        color: "#F97316",
        group: "Employment Details",
      };
    })(),
    (() => {
      const experienceOrder = [
        "0-2 years",
        "3-5 years",
        "6-10 years",
        "10+ years",
      ];
      const sortedExperienceData = experienceData.sort((a, b) => {
        return (
          experienceOrder.indexOf(a.name) - experienceOrder.indexOf(b.name)
        );
      });
      return {
        id: "experience",
        title: "Distribution by Experience Level",
        icon: null,
        type: "bar",
        layout: "vertical",
        data: sortedExperienceData,
        xAxisData: sortedExperienceData.map((d) => d.name),
        yAxisData: sortedExperienceData.map((d) => d.name),
        seriesData: [
          {
            data: sortedExperienceData.map((d) => d.value),
            label: "Employees",
            color: "#10B981",
            valueFormatter: (value) => value.toString(),
          },
        ],
        color: "#10B981",
        group: "Employment Details",
      };
    })(),
    (() => {
      const sortedOrganizationData = organizationData.sort(
        (a, b) => b.value - a.value
      );
      return {
        id: "organization",
        title: "Distribution by Organization Units",
        icon: null,
        type: "bar",
        data: sortedOrganizationData,
        xAxisData: sortedOrganizationData.map((d) => d.name),
        seriesData: [
          {
            data: sortedOrganizationData.map((d) => d.value),
            label: "Employees",
            color: "#3B82F6",
          },
        ],
        color: "#3B82F6",
        group: "Employment Details",
      };
    })(),
    (() => {
      const sortedDesignationData = designationData.sort(
        (a, b) => b.value - a.value
      );
      return {
        id: "designation",
        title: "Distribution by Designation",
        icon: null,
        type: "bar",
        layout: "horizontal",
        data: sortedDesignationData,
        yAxisData: sortedDesignationData.map((d) => d.name),
        seriesData: [
          {
            data: sortedDesignationData.map((d) => d.value),
            label: "Employees",
            color: "#8B5CF6",
            valueFormatter: (value) => value.toString(),
          },
        ],
        color: "#8B5CF6",
        group: "Employment Details",
      };
    })(),
  ];



  // const allWidgetConfigs = [
  //   {
  //     id: "totalworkforce",
  //     title: "Total Workforce",
  //     icon: null,
  //     color: "#3B82F6",
  //     group: "Total Workforce",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc`,
  //     value: employees.length,
  //   },
  //   {
  //     id: "totalEmployees",
  //     title: "Employees",
  //     icon: null,
  //     color: "#3B82F6",
  //     group: "Total Workforce",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=employee`,
  //     value: employees.length - freelancers.length,
  //   },
  //  {
  //     id: "totalFreelancers",
  //     title: "Freelancers",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Total Workforce",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=freelancer`,
  //     value: employees.length   - (employees.length - freelancers.length),
  //   },
  //   {
  //     id: "totalEmployees",
  //     title: "Employees",
  //     icon: null,
  //     color: "#3B82F6",
  //     group: "Employees",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=employee`,
  //     value: employees.length,
  //   },
  //   {
  //     id: "newJoiningThisMonth",
  //     title: "New Joining This Month",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Employees",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_Joining_Month=this_month`,
  //     value: calculateWidgetValues.newJoining,
  //   },

  //   {
  //     id: "totalInterns",
  //     title: "Interns",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Interns",
  //     value: interns.length,
  //   },

  //   {
  //     id: "newJoiningThisMonthInterns",
  //     title: "New Joining This Month",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Interns",
  //     value: calculateInternWidgetValues.newJoining,
  //   },

  //   {
  //     id: "totalFreelancers",
  //     title: "Freelancers",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Freelancers",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=freelancer`,
  //     value: freelancers.length,
  //   },

  //   {
  //     id: "newJoiningThisMonthFreelancers",
  //     title: "New Joining This Month",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Freelancers",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_Joining_Month=this_month`,
  //     value: calculateInternWidgetValues.newJoining,
  //   },

  //   {
  //     id: "birthdayThisMonth",
  //     title: "Birthday This Month",
  //     icon: null,
  //     color: "#EC4899",
  //     group: "Occasions",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_Birth_Month=this_month`,
  //     value: calculateWidgetValues.birthday,
  //   },
  //   {
  //     id: "workAnniversariesThisMonth",
  //     title: "Work Anniversaries This Month",
  //     icon: null,
  //     color: "#F59E0B",
  //     group: "Occasions",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_Work_Anniversary=this_month`,
  //     value: calculateWidgetValues.anniversary,
  //   },

  //   {
  //     id: "presentToday",
  //     title: "Present Today",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Time & Attendance",
  //     value: attendanceStats.present,
  //   },
  //   {
  //     id: "absentToday",
  //     title: "Absent Today",
  //     icon: null,
  //     color: "#EF4444",
  //     group: "Time & Attendance",
  //     value: attendanceStats.absent,
  //   },
  //   {
  //     id: "lateCheckInsToday",
  //     title: "Late Check-ins Today",
  //     icon: null,
  //     color: "#F59E0B",
  //     group: "Time & Attendance",
  //     value: attendanceStats.late,
  //   },

  //   {
  //     id: "onLeaveToday",
  //     title: "On Leave Today",
  //     icon: null,
  //     color: "#3B82F6",
  //     group: "Leaves",
  //     value: leavesStats.onLeaveToday,
  //   },
  //   {
  //     id: "upcomingLeaves",
  //     title: "Upcoming Leaves (7d)",
  //     icon: null,
  //     color: "#8B5CF6",
  //     group: "Leaves",
  //     value: leavesStats.upcomingLeaves,
  //   },
  //   {
  //     id: "pendingLeaveRequests",
  //     title: "Pending Leave Requests",
  //     icon: null,
  //     color: "#F97316",
  //     group: "Leaves",
  //     value: leavesStats.pending,
  //   },
  // ];


  const allWidgetConfigs = [
    {
      id: "totalworkforce",
      title: "Total Workforce",
      icon: null,
      color: "#3B82F6",
      group: "Total Workforce",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc`,
      value: employees.length,
    },
    {
      id: "totalEmployees",
      title: "Employees",
      icon: null,
      color: "#3B82F6",
      group: "Total Workforce",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=Employee&filter_employment_type_operator=equals`,
      value: employees.length - freelancers.length,
    },
    {
      id: "totalFreelancers",
      title: "Freelancers",
      icon: null,
      color: "#10B981",
      group: "Total Workforce",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=Freelancer&filter_employment_type_operator=equals`,
      value: employees.length - (employees.length - freelancers.length),
    },
    {
      id: "totalEmployees",
      title: "Employees",
      icon: null,
      color: "#3B82F6",
      group: "Employees",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=Employee&filter_employment_type_operator=equals`,
      value: employees.length,
    },
    {
      id: "newJoiningThisMonth",
      title: "New Joining This Month",
      icon: null,
      color: "#10B981",
      group: "Employees",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_date_of_joining=&filter_date_of_joining_operator=this_month_joining`,
      value: calculateWidgetValues.newJoining,
    },

    {
      id: "totalInterns",
      title: "Interns",
      icon: null,
      color: "#10B981",
      group: "Interns",
      value: interns.length,
    },

    {
      id: "newJoiningThisMonthInterns",
      title: "New Joining This Month",
      icon: null,
      color: "#10B981",
      group: "Interns",
      value: calculateInternWidgetValues.newJoining,
    },

    {
      id: "totalFreelancers",
      title: "Freelancers",
      icon: null,
      color: "#10B981",
      group: "Freelancers",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=Freelancer&filter_employment_type_operator=equals`,
      value: freelancers.length,
    },

    {
      id: "newJoiningThisMonthFreelancers",
      title: "New Joining This Month",
      icon: null,
      color: "#10B981",
      group: "Freelancers",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_date_of_joining=&filter_date_of_joining_operator=this_month_joining`,
      value: calculateFreelancerWidgetValues.newJoining,
    },

    {
      id: "birthdayThisMonth",
      title: "Birthday This Month",
      icon: null,
      color: "#EC4899",
      group: "Occasions",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_date_of_birth=&filter_date_of_birth_operator=this_month`,
      value: calculateWidgetValues.birthday,
    },
    {
      id: "workAnniversariesThisMonth",
      title: "Work Anniversaries This Month",
      icon: null,
      color: "#F59E0B",
      group: "Occasions",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_date_of_joining=&filter_date_of_joining_operator=this_month_anniversary`,
      value: calculateWidgetValues.anniversary,
    },

    {
      id: "presentToday",
      title: "Present Today",
      icon: null,
      color: "#10B981",
      group: "Time & Attendance",
      value: attendanceStats.present,
    },
    {
      id: "absentToday",
      title: "Absent Today",
      icon: null,
      color: "#EF4444",
      group: "Time & Attendance",
      value: attendanceStats.absent,
    },
    {
      id: "lateCheckInsToday",
      title: "Late Check-ins Today",
      icon: null,
      color: "#F59E0B",
      group: "Time & Attendance",
      value: attendanceStats.late,
    },

    {
      id: "onLeaveToday",
      title: "On Leave Today",
      icon: null,
      color: "#3B82F6",
      group: "Leaves",
      value: leavesStats.onLeaveToday,
    },
    {
      id: "upcomingLeaves",
      title: "Upcoming Leaves (7d)",
      icon: null,
      color: "#8B5CF6",
      group: "Leaves",
      value: leavesStats.upcomingLeaves,
    },
    {
      id: "pendingLeaveRequests",
      title: "Pending Leave Requests",
      icon: null,
      color: "#F97316",
      group: "Leaves",
      value: leavesStats.pending,
    },
  ];

 

  const [visibleChartIds, setVisibleChartIds] = React.useState([]);
  const [visibleWidgetIds, setVisibleWidgetIds] = React.useState([]);
  const [chartViewModes, setChartViewModes] = React.useState({});

  React.useEffect(() => {
    const savedChartSettings = localStorage.getItem("chartDisplaySettings");
    const savedWidgetSettings = localStorage.getItem("widgetDisplaySettings");

    if (savedChartSettings) {
      const parsedSettings = JSON.parse(savedChartSettings) || [];
      const savedMap = new Map(
        parsedSettings.map((s) => [
          s.id,
          { isVisible: !!s.isVisible, viewMode: s.viewMode || "chart" },
        ])
      );

      const mergedVisibleChartIds = allChartConfigs
        .filter((config) => {
          const saved = savedMap.get(config.id);
          return saved ? saved.isVisible : true;
        })
        .map((c) => c.id);

      setVisibleChartIds(mergedVisibleChartIds);

      const modes = {};
      allChartConfigs.forEach((chart) => {
        const saved = savedMap.get(chart.id);
        modes[chart.id] = saved?.viewMode || "chart";
      });
      setChartViewModes(modes);
    } else {
      setVisibleChartIds(allChartConfigs.map((config) => config.id));
      const modes = {};
      allChartConfigs.forEach((chart) => (modes[chart.id] = "chart"));
      setChartViewModes(modes);
    }

    if (savedWidgetSettings) {
      const parsedSettings = JSON.parse(savedWidgetSettings) || [];
      const savedMap = new Map(
        parsedSettings.map((s) => [s.id, !!s.isVisible])
      );

      const mergedVisibleWidgetIds = allWidgetConfigs
        .filter((config) => {
          const savedVisible = savedMap.get(config.id);
          return savedVisible !== undefined ? savedVisible : true;
        })
        .map((w) => w.id);

      setVisibleWidgetIds(mergedVisibleWidgetIds);
    } else {
      setVisibleWidgetIds(allWidgetConfigs.map((config) => config.id));
    }
  }, []);

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };


  const handleWidgetClick = (widgetId, widgetTitle) => {
    if (widgetId === "totalworkforce") {
      navigate("/organization/employee/employee-details", { state: { fromDashboard: true, widgetId ,employmentType: "all" } });
      return;
    }
    if (widgetId === "totalEmployees" || widgetId === "newJoiningThisMonth") {
      navigate("/organization/employee/employee-details", { state: { fromDashboard: true, widgetId ,employmentType: "employee" } });
      return;
    }
    if (widgetId === "totalFreelancers" || widgetId === "newJoiningThisMonthFreelancers") {
      navigate("/organization/freelancer/freelancer-details", { state: { fromDashboard: true, widgetId ,employmentType: "freelancer" } });
      return;
    }
    if (widgetId === "totalInterns" || widgetId === "newJoiningThisMonthInterns") {
      navigate("/organization/intern/intern-details", { state: { fromDashboard: true, widgetId } });
      return;
    }
    if (widgetId === "birthdayThisMonth" || widgetId === "workAnniversariesThisMonth") {
      navigate("/organization/employee/employee-details", { state: { fromDashboard: true, widgetId } });
      return;
    }
    if (widgetId === "presentToday" || widgetId === "absentToday" || widgetId === "lateCheckInsToday") {
      navigate("/attendance/time-logs", { state: { fromDashboard: true, widgetId } });
      return;
    }
    if (widgetId === "onLeaveToday" || widgetId === "upcomingLeaves" || widgetId === "pendingLeaveRequests") {
      navigate("/leave/employee-leaves", { state: { fromDashboard: true, widgetId } });
      return;
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenPersonalizationPage = () => {
    navigate("dashboard/personalize-dashboard");
    handleCloseMenu();
  };

  const open = Boolean(anchorEl);

  // Lead summary + training program graph (EMS org dashboard)
  if (org) {
    const totalStudents = leadsSummary?.total_students ?? 0;
    const studentsAddedToday = leadsSummary?.students_added_today ?? 0;
    const feesDueThisMonth =
      feeInstallmentDueCounts?.monthPending ?? leadsSummary?.fees_due_this_month ?? 0;
    const feesDueToday =
      feeInstallmentDueCounts?.todayPending ?? leadsSummary?.fees_due_today ?? 0;
    const feesOverdue =
      feeInstallmentDueCounts?.overdue ?? leadsSummary?.fees_overdue ?? 0;
    const feesDueThisMonthAmount =
      feeInstallmentDueCounts?.monthPendingAmount ??
      (feeInstallmentDueCounts?.monthPending !== null
        ? 0
        : leadsSummary?.fees_due_amount_this_month_inr ?? null);
    const feesDueTodayAmount =
      feeInstallmentDueCounts?.todayPendingAmount ??
      (feeInstallmentDueCounts?.todayPending !== null ? 0 : null);
    const feesOverdueAmount =
      feeInstallmentDueCounts?.overdueAmount ??
      (feeInstallmentDueCounts?.overdue !== null ? 0 : null);

    const formatDashboardInr = (amount) => {
      const value = Number(amount);
      if (!Number.isFinite(value)) return "—";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(value);
    };

    const feeCardAmountDisplay = (amount) => {
      if (amount === null || amount === undefined) return "…";
      return formatDashboardInr(amount);
    };

    const currentStats = {
      trainingPrograms: leadsSummary?.active_training_programs_till_date ?? 0,
      trainers: leadsSummary?.active_trainers_till_date ?? 0,
      trainerUsers: Array.isArray(leadsSummary?.trainer_users)
        ? leadsSummary.trainer_users
        : [],
      batches: leadsSummary?.active_batches_till_date ?? 0,
      students:
        leadsSummary?.batch_students_completed_till_date ??
        leadsSummary?.total_students ??
        0,
      openLeads: leadsSummary?.open_leads ?? 0,
    };

    const extendedStats = {
      totalCapacity:
        leadsSummary?.total_student_capacity ??
        leadsSummary?.total_training_spaces_capacity ??
        800,
      trainingSpacesCount: leadsSummary?.training_spaces_count ?? 0,
      feesDueAmountThisMonth: leadsSummary?.fees_due_amount_this_month_inr ?? 0,
      feesCollectedThisMonth: leadsSummary?.fees_collected_this_month ?? 0,
      feesCollectedAmountThisMonth:
        leadsSummary?.fees_collected_amount_this_month_inr ?? 0,
      admissionsThisMonth: leadsSummary?.admissions_this_month ?? 0,
    };

    const navigateActiveBatchStudents = () => {
      navigate(
        "/batch-students?batch_status=Pursuing&batch_run_status=Ongoing&page=1&pageSize=25"
      );
    };

    const navigateOpenLeadsList = () => {
      const params = new URLSearchParams({
        lead_status: "open",
        page: "1",
        pageSize: "25",
        sortBy: "lead_datetime",
        sortOrder: "desc",
      });
      navigate(`/leads?${params.toString()}`);
    };

    const navigateFeesCollectedThisMonth = () => {
      const params = new URLSearchParams({
        period: "month",
        page: "1",
        pageSize: "25",
        sortBy: "payment_date",
        sortOrder: "desc",
      });
      navigate(`/students-fees?${params.toString()}`);
    };

    const navigateUsersByRole = (roleName) => {
      const slug = String(roleName || "").toLowerCase();
      if (slug === "trainer") {
        navigateToResourceRole(navigate, "trainers");
        return;
      }
      if (slug === "counselor") {
        navigateToResourceRole(navigate, "counselors");
        return;
      }
      const params = new URLSearchParams({
        page: "1",
        pageSize: "10",
        sortBy: "application_user_full_name",
        sortOrder: "asc",
        filter_application_user_role_id_name: roleName,
        filter_application_user_role_id_name_operator: "equals",
      });
      navigate(`/users?${params.toString()}`);
    };

    const tpGraph = leadsSummary?.training_program_graph;
    const tpCategories = Array.isArray(tpGraph?.categories)
      ? tpGraph.categories
      : [];

    const { bars: chartBars, maxVal: chartMax } = trainingChartData;
    const yAxisValues = (() => {
      if (!Number.isFinite(chartMax) || chartMax <= 0) return [0];

      // For small integer counts, show exact ticks (2,1,0) without duplicates.
      if (Number.isInteger(chartMax) && chartMax <= 8) {
        return Array.from({ length: chartMax + 1 }, (_, i) => chartMax - i);
      }

      // For larger values, keep axis compact with evenly spaced ticks.
      const steps = 5;
      return Array.from({ length: steps + 1 }, (_, i) => {
        const raw = chartMax - (chartMax / steps) * i;
        const value = chartMax <= 10 ? Math.round(raw * 10) / 10 : Math.round(raw);
        return i === steps ? 0 : value;
      });
    })();

    const handleTrainingGraphNavigate = (programName, categoryName, programId) => {
      const params = new URLSearchParams();
      if (programId) {
        params.set("training_program", String(programId));
        params.set("training_program_id", String(programId));
      } else if (programName) {
        params.set("training_program", programName);
      }
      if (categoryName) params.set("training_program_category", categoryName);
      const query = params.toString();
      navigate(`/admissions${query ? `?${query}` : ""}`);
    };

    const navigateFeeInstallments = (filterParams = {}) => {
      const params = new URLSearchParams({
        page: "1",
        pageSize: "25",
        sortBy: "due_date",
        sortOrder: "asc",
      });
      Object.entries(filterParams).forEach(([key, value]) => {
        const text = String(value ?? "").trim();
        if (text) params.set(key, text);
      });
      navigate(`/fee-installments?${params.toString()}`);
    };

    const EMS_DASHBOARD_SECTION_KEYS = [
      "Institute_Overview",
      "Fees_Collected",
      "Admissions",
      "Current_Batches",
      "Training_Program_Graph",
    ];
    const showInstituteOverview =
      !isTrainerWorkspace && hasEmsDashboardSection("Institute_Overview");
    const showFeesCollected =
      !isTrainerWorkspace && hasEmsDashboardSection("Fees_Collected");
    const showAdmissions =
      !isTrainerWorkspace && hasEmsDashboardSection("Admissions");
    const showCurrentBatches = hasEmsDashboardSection("Current_Batches");
    const showTrainingProgramGraph = hasEmsDashboardSection("Training_Program_Graph");
    const hasAnyEmsDashboardSection = EMS_DASHBOARD_SECTION_KEYS.some((key) =>
      hasEmsDashboardSection(key)
    );
    const showMonthlyChartsSection = showFeesCollected || showAdmissions;

    const initials = (name) => {
      const parts = String(name || "").trim().split(/\s+/);
      return parts.map((p) => p[0] || "").join("").toUpperCase().slice(0, 2);
    };

    const chartDisplayData = trainingChartData.bars.map((b) => ({
      name: b.programName,
      total: b.total,
      programId: b.programId ?? null,
    }));

    const FeesMonthlyTooltip = ({ active, payload }) => {
      if (!active || !payload?.length) return null;
      const row = payload[0]?.payload;
      if (!row) return null;
      return (
        <div
          style={{
            background: "var(--ems-surface)",
            border: "1px solid var(--ems-border)",
            borderRadius: 10,
            padding: "10px 12px",
            fontSize: 13,
            fontFamily: "Manrope, sans-serif",
            color: "var(--ems-text)",
          }}
        >
          <div style={{ fontWeight: 650, marginBottom: 4 }}>
            {formatMonthChartLabel(row.month)}
          </div>
          <div>
            Collected : ₹
            {Number(row.amount ?? 0).toLocaleString("en-IN", {
              maximumFractionDigits: 2,
            })}
          </div>
          {row.paymentCount != null && (
            <div>Payments : {row.paymentCount}</div>
          )}
        </div>
      );
    };

    const AdmissionsMonthlyTooltip = ({ active, payload }) => {
      if (!active || !payload?.length) return null;
      const row = payload[0]?.payload;
      if (!row) return null;
      return (
        <div
          style={{
            background: "var(--ems-surface)",
            border: "1px solid var(--ems-border)",
            borderRadius: 10,
            padding: "10px 12px",
            fontSize: 13,
            fontFamily: "Manrope, sans-serif",
            color: "var(--ems-text)",
          }}
        >
          <div style={{ fontWeight: 650, marginBottom: 4 }}>
            {formatMonthChartLabel(row.month)}
          </div>
          <div>Admissions : {row.count}</div>
        </div>
      );
    };

    const TrainingProgramTooltip = ({ active, payload }) => {
      if (!active || !payload?.length) return null;
      const row = payload[0]?.payload;
      if (!row) return null;
      return (
        <div
          style={{
            background: "var(--ems-surface)",
            border: "1px solid var(--ems-border)",
            borderRadius: 10,
            padding: "10px 12px",
            fontSize: 13,
            fontFamily: "Manrope, sans-serif",
            color: "var(--ems-text)",
          }}
        >
          <div style={{ fontWeight: 650, marginBottom: 4 }}>{row.name}</div>
          <div>Students : {row.total}</div>
        </div>
      );
    };

    if (!isTrainerWorkspace && isCounsellorUser) {
      return <CounsellorDashboard />;
    }

    return (
      <div className="ems-dashboard ems-fade-up">
        {/* Page head */}
        <div className="ems-page-head">
          <div>
            <h1 className="ems-page-title">
              {isTrainerWorkspace ? "Classes" : "Dashboard"}
            </h1>
            <div className="ems-page-sub">
              <span>{formattedDate}</span>
            </div>
          </div>
        </div>

        {!isTrainerWorkspace && isAccountantUser && (
        <section className="ems-stat-cards-brand" style={{ marginBottom: 8 }}>
          <div className="ems-grid ems-g-2">
            {[
              {
                label: "Fee Collected This Month",
                value: leadsSummaryLoading
                  ? "…"
                  : formatDashboardInr(extendedStats.feesCollectedAmountThisMonth),
                icon: <IndianRupee size={20} />,
                tone: "ok",
                valueTone: "ok",
                onClick: navigateFeesCollectedThisMonth,
              },
              {
                label: "Pending Fee",
                value: leadsSummaryLoading
                  ? "…"
                  : formatDashboardInr(extendedStats.feesDueAmountThisMonth),
                icon: <CalendarClock size={20} />,
                tone: "bad",
                valueTone: "bad",
                onClick: () =>
                  navigateFeeInstallments({
                    status: "pending",
                    due_period: "this_month",
                  }),
              },
            ].map((s) => (
              <div
                key={s.label}
                className="ems-card ems-stat"
                style={{ cursor: "pointer" }}
                onClick={s.onClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") s.onClick();
                }}
              >
                <div className="ems-stat-top">
                  <div className={`ems-stat-icon${s.tone ? ` ${s.tone}` : ""}`}>
                    {s.icon}
                  </div>
                </div>
                <div className="ems-stat-label">{s.label}</div>
                <div className={`ems-stat-value ems-tnum${s.valueTone ? ` ${s.valueTone}` : ""}`}>
                  {s.value}
                </div>
              </div>
            ))}
          </div>
        </section>
        )}

        {leadsSummaryError && (
          <div
            style={{ color: "var(--ems-red)", fontSize: 13, marginBottom: 12 }}
          >
            {leadsSummaryError}
          </div>
        )}

        {!hasAnyEmsDashboardSection && (
          <section className="ems-card" style={{ padding: "28px 24px", textAlign: "center" }}>
            <div style={{ color: "var(--ems-faint)", fontSize: 14 }}>
              You do not have permission to view any dashboard sections.
            </div>
          </section>
        )}










        {/* Institute Overview */}
        {showInstituteOverview && (
          <section className="ems-institute-overview ems-stat-cards-brand">
            <div className="ems-sec-head">
              <div className="ems-sec-title">
                <span className="ems-bar" />
                Institute Overview
              </div>
            </div>
            <div className="ems-grid ems-g-4">
              {[
                {
                  label: "Active Students",
                  value: currentStats.students,
                  icon: <GraduationCap size={20} />,
                  onClick: navigateActiveBatchStudents,
                },
                {
                  label: "Fees Collected This month",
                  value: leadsSummaryLoading
                    ? "…"
                    : formatDashboardInr(
                        extendedStats.feesCollectedAmountThisMonth
                      ),
                  // sublabel: leadsSummaryLoading
                  //   ? ""
                  //   : `${extendedStats.feesCollectedThisMonth} payment${
                  //       extendedStats.feesCollectedThisMonth === 1 ? "" : "s"
                  //     } this month`,
                  icon: <IndianRupee size={20} />,
                  tone: "ok",
                  valueTone: "ok",
                  onClick: navigateFeesCollectedThisMonth,
                },
                {
                  label: "Fees Due This month",
                  value: leadsSummaryLoading
                    ? "…"
                    : formatDashboardInr(extendedStats.feesDueAmountThisMonth),
                  // sublabel: "Pending installments this month",
                  icon: <CalendarClock size={20} />,
                  tone: "bad",
                  valueTone: "bad",
                  onClick: () =>
                    navigateFeeInstallments({
                      status: "pending",
                      due_period: "this_month",
                    }),
                },
                {
                  label: "Open Leads",
                  value: currentStats.openLeads,
                  icon: <Target size={20} />,
                  onClick: navigateOpenLeadsList,
                },
              ].map((s) => (
                <div
                  key={s.label}
                  className="ems-card ems-stat"
                  style={s.onClick ? { cursor: "pointer" } : undefined}
                  title={s.title}
                  onClick={s.onClick}
                  role={s.onClick ? "button" : undefined}
                  tabIndex={s.onClick ? 0 : undefined}
                  onKeyDown={
                    s.onClick
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") s.onClick();
                        }
                      : undefined
                  }
                >
                  <div className="ems-stat-top">
                    <div className={`ems-stat-icon${s.tone ? ` ${s.tone}` : ""}`}>
                      {s.icon}
                    </div>
                  </div>
                  <div className="ems-stat-label">{s.label}</div>
                  <div
                    className={`ems-stat-value ems-tnum${
                      s.valueTone ? ` ${s.valueTone}` : ""
                    }`}
                  >
                    {leadsSummaryLoading && typeof s.value === "number"
                      ? "…"
                      : s.value}
                  </div>
                  {s.sublabel ? (
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--ems-faint)",
                        marginTop: 6,
                      }}
                    >
                      {s.sublabel}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
            <div className="ems-grid ems-g-4" style={{ marginTop: 16 }}>
              {[
                {
                  label: "Total Capacity",
                  value: extendedStats.totalCapacity,
                  sublabel:
                    extendedStats.totalCapacity > 0
                      ? `${currentStats.students} / ${extendedStats.totalCapacity} active students`
                      : "Institute student capacity",
                  icon: <Building2 size={20} />,
                },
                {
                  label: "Trainers",
                  value: currentStats.trainers,
                  icon: <UserCog size={20} />,
                  onClick: () => navigateUsersByRole("Trainer"),
                  title:
                    currentStats.trainerUsers.length > 0
                      ? currentStats.trainerUsers
                          .map((t) => t.full_name)
                          .filter(Boolean)
                          .join(", ")
                      : undefined,
                },
                {
                  label: "Batches",
                  value: currentStats.batches,
                  icon: <Boxes size={20} />,
                  onClick: () => navigate("/batches?status=Ongoing"),
                },
                {
                  label: "Admissions This month",
                  value: extendedStats.admissionsThisMonth,
                  // sublabel: "New admissions this month",
                  icon: <ClipboardList size={20} />,
                  onClick: () => navigate("/admissions?period=month"),
                },
              ].map((s) => (
                <div
                  key={s.label}
                  className="ems-card ems-stat"
                  style={s.onClick ? { cursor: "pointer" } : undefined}
                  title={s.title}
                  onClick={s.onClick}
                  role={s.onClick ? "button" : undefined}
                  tabIndex={s.onClick ? 0 : undefined}
                  onKeyDown={
                    s.onClick
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") s.onClick();
                        }
                      : undefined
                  }
                >
                  <div className="ems-stat-top">
                    <div className={`ems-stat-icon${s.tone ? ` ${s.tone}` : ""}`}>
                      {s.icon}
                    </div>
                  </div>
                  <div className="ems-stat-label">{s.label}</div>
                  <div
                    className={`ems-stat-value ems-tnum${
                      s.valueTone ? ` ${s.valueTone}` : ""
                    }`}
                  >
                    {leadsSummaryLoading ? "…" : s.value}
                  </div>
                  {s.sublabel ? (
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--ems-faint)",
                        marginTop: 6,
                      }}
                    >
                      {s.sublabel}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Monthly fees & admissions graphs */}
        {showMonthlyChartsSection && (
          <section className="ems-monthly-charts-grid" style={{ marginTop: 24 }}>
            {showFeesCollected && (
            <div className="ems-card ems-chart-card">
              <div className="ems-chart-head">
                <div>
                  <div className="ems-sec-title" style={{ fontSize: 17 }}>
                    <span className="ems-bar" />
                    Fees Collected
                  </div>
                  <p className="ems-chart-desc">
                    {formatMonthlyChartPeriod(feesCollectedMonthlyChart.period)}
                  </p>
                </div>
              </div>
              <div style={{ width: "100%", height: CHART_PLOT_HEIGHT }}>
                {leadsSummaryLoading ? (
                  <div className="ems-chart-empty">Loading chart…</div>
                ) : feesCollectedMonthlyChart.bars.length === 0 ? (
                  <div className="ems-chart-empty">No fee data for this period</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={feesCollectedMonthlyChart.bars}
                      margin={{ top: 8, right: 12, left: 4, bottom: 32 }}
                    >
                      <CartesianGrid {...MONTHLY_CHART_GRID_PROPS} />
                      <XAxis dataKey="monthTick" {...MONTHLY_CHART_X_AXIS_PROPS} />
                      <YAxis
                        domain={[0, feesChartYAxisConfig.max]}
                        ticks={feesChartYAxisConfig.ticks}
                        tickFormatter={formatFeesChartYAxis}
                        tick={{ fontSize: 11, fill: "var(--ems-muted)" }}
                        axisLine={false}
                        tickLine={false}
                        width={56}
                        allowDecimals={false}
                      />
                      <RechartsTooltip content={<FeesMonthlyTooltip />} />
                      <Bar
                        dataKey="amount"
                        fill="var(--ems-chart-1)"
                        shape={renderMonthlyStandingBar}
                        maxBarSize={42}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
              {!leadsSummaryLoading && (
                <div className="ems-chart-foot">
                  <strong>
                    ₹
                    {feesCollectedMonthlyChart.total.toLocaleString("en-IN", {
                      maximumFractionDigits: 2,
                    })}
                  </strong>{" "}
                  total collection
                </div>
              )}
            </div>
            )}

            {showAdmissions && (
            <div className="ems-card ems-chart-card">
              <div className="ems-chart-head">
                <div>
                  <div className="ems-sec-title" style={{ fontSize: 17 }}>
                    <span className="ems-bar" />
                    Admissions
                  </div>
                  <p className="ems-chart-desc">
                    {formatMonthlyChartPeriod(admissionsMonthlyChart.period)}
                  </p>
                </div>
              </div>
              <div style={{ width: "100%", height: CHART_PLOT_HEIGHT }}>
                {leadsSummaryLoading ? (
                  <div className="ems-chart-empty">Loading chart…</div>
                ) : admissionsMonthlyChart.points.length === 0 ? (
                  <div className="ems-chart-empty">No admission data for this period</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={admissionsMonthlyChart.points}
                      margin={{ top: 8, right: 12, left: 4, bottom: 32 }}
                    >
                      <CartesianGrid {...MONTHLY_CHART_GRID_PROPS} />
                      <XAxis dataKey="monthTick" {...MONTHLY_CHART_X_AXIS_PROPS} />
                      <YAxis
                        domain={[0, admissionsChartYAxisConfig.max]}
                        ticks={admissionsChartYAxisConfig.ticks}
                        allowDecimals={false}
                        tick={{ fontSize: 11, fill: "var(--ems-muted)" }}
                        axisLine={false}
                        tickLine={false}
                        width={40}
                      />
                      <RechartsTooltip content={<AdmissionsMonthlyTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="var(--ems-chart-2)"
                        strokeWidth={2.5}
                        dot={({ cx, cy, payload }) =>
                          Number(payload?.count) > 0 ? (
                            <circle
                              cx={cx}
                              cy={cy}
                              r={4}
                              fill="var(--ems-chart-2)"
                              strokeWidth={0}
                            />
                          ) : null
                        }
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
              {!leadsSummaryLoading && (
                <div className="ems-chart-foot">
                  <strong>{admissionsMonthlyChart.total}</strong> total admissions
                </div>
              )}
            </div>
            )}
          </section>
        )}

        {/* Batch Trainer Details */}
        {showCurrentBatches && (
          <section style={{ marginTop: 24 }}>
            <div className="ems-card ems-table-card">
              <div className="ems-table-toolbar">
                <Boxes
                  size={18}
                  style={{ color: "var(--ems-primary)", flexShrink: 0 }}
                />
                <span className="ems-tt-title">Current Batches</span>
                {!hasLoggedInTrainerAssignments &&
                  trainerOptions.length > 0 && (
                    <select
                      className="ems-select"
                      value={effectiveSelectedBatchTrainerId}
                      onChange={(e) =>
                        setSelectedBatchTrainerId(e.target.value)
                      }
                    >
                      <option value="">All Trainers</option>
                      {trainerOptions.map((opt) => (
                        <option
                          key={opt.trainer_employee_id}
                          value={String(opt.trainer_employee_id)}
                        >
                          {batchTrainerLabel(opt)}
                        </option>
                      ))}
                    </select>
                  )}
                <button
                  className="ems-btn ems-sm"
                  onClick={exportBatchTrainerDetailsToExcel}
                  style={{ marginLeft: "auto" }}
                >
                  <Download size={14} />
                  Export
                </button>
              </div>
              <div className="ems-table-wrap">
                <table className="ems-data">
                  <thead>
                    <tr>
                      <th>Batch</th>
                      <th>Training Program</th>
                      <th>Trainer</th>
                      <th className="ems-num">Students</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Batch Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedBatchTrainerDisplayRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          style={{
                            textAlign: "center",
                            color: "var(--ems-faint)",
                            padding: "30px 18px",
                          }}
                        >
                          No batches found.
                        </td>
                      </tr>
                    ) : (
                      groupedBatchTrainerDisplayRows.map((group) => {
                        const days = group.batchDays;
                        const daysArr = Array.isArray(days) ? days : [];
                        const timingLines = group.timingLines || [];
                        const lineStackStyle = (index) =>
                          index > 0
                            ? {
                                borderTop: "1px solid var(--ems-border)",
                                marginTop: 8,
                                paddingTop: 8,
                              }
                            : undefined;

                        const renderTrainerLine = (line) => {
                          const trainers = line.trainerNames || [];
                          if (trainers.length === 0) return "—";
                          return (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 6,
                              }}
                            >
                              {trainers.map((trainer, index) => {
                                const tname =
                                  typeof trainer === "string"
                                    ? trainer
                                    : trainer?.name || "—";
                                const imageUrl =
                                  typeof trainer === "string"
                                    ? ""
                                    : trainer?.imageUrl || "";
                                const trainerId =
                                  typeof trainer === "string"
                                    ? `${tname}-${index}`
                                    : trainer?.id || `${tname}-${index}`;
                                return (
                                  <div
                                    key={String(trainerId)}
                                    className="ems-trainer-cell"
                                    style={trainerDetailsSingleLineCellSx}
                                  >
                                    <ListPersonAvatar
                                      key={`avatar-${trainerId}-${imageUrl || "none"}`}
                                      imageUrl={imageUrl}
                                      size={28}
                                    />
                                    {tname}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        };

                        return (
                          <tr key={group.batchId}>
                            <td>
                              <span className="ems-batch-tag">
                                {group.batchCode}
                              </span>
                            </td>
                            <td className="ems-prog-cell">
                              {group.trainingProgram}
                            </td>
                            <td>
                              {timingLines.map((line, index) => (
                                <div
                                  key={line.slotKey}
                                  style={lineStackStyle(index)}
                                >
                                  {renderTrainerLine(line)}
                                </div>
                              ))}
                            </td>
                            <td className="ems-num">
                              {timingLines.map((line, index) => (
                                <div
                                  key={`${line.slotKey}-students`}
                                  style={lineStackStyle(index)}
                                >
                                  <span className="ems-stud-badge ems-tnum">
                                    {line.studentCount}
                                  </span>
                                </div>
                              ))}
                            </td>
                            <td>
                              {timingLines.map((line, index) => (
                                <div
                                  key={`${line.slotKey}-start`}
                                  style={lineStackStyle(index)}
                                >
                                  <span className="ems-time-pill">
                                    {formatTimeAmPm(line.startTimeRaw)}
                                  </span>
                                </div>
                              ))}
                            </td>
                            <td>
                              {timingLines.map((line, index) => (
                                <div
                                  key={`${line.slotKey}-end`}
                                  style={lineStackStyle(index)}
                                >
                                  <span className="ems-time-pill">
                                    {formatTimeAmPm(line.endTimeRaw)}
                                  </span>
                                </div>
                              ))}
                            </td>
                            <td>
                              {daysArr.length > 0 ? (
                                <div className="ems-days-chips">
                                  {daysArr.map((d) => (
                                    <span key={d} className="ems-day-chip">
                                      {shortBatchDay(d)}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="ems-trend-note">
                                  {formatBatchDays(days) || "—"}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {/* Monthly fees & admissions graphs */}
        {showTrainingProgramGraph && (
          <section style={{ marginTop: 24 }}>
            <div className="ems-card ems-chart-card">
              <div className="ems-chart-head">
                <div>
                  <div className="ems-sec-title" style={{ fontSize: 17 }}>
                    <span className="ems-bar" />
                    Training Program Graph
                  </div>
                  <p className="ems-chart-desc">
                    Students by program (admissions) —{" "}
                    <strong>
                      {chartDisplayData.reduce((a, b) => a + b.total, 0)}
                    </strong>{" "}
                    total
                    {trainingGraphCategory !== "all" &&
                      tpCategories[trainingGraphCategory] && (
                        <>
                          {" "}
                          ·{" "}
                          <strong>
                            {tpCategories[trainingGraphCategory]}
                          </strong>
                        </>
                      )}
                  </p>
                </div>
                {tpCategories.length > 0 && (
                  <select
                    className="ems-select"
                    value={
                      trainingGraphCategory === "all"
                        ? "all"
                        : trainingGraphCategory
                    }
                    onChange={(e) =>
                      setTrainingGraphCategory(
                        e.target.value === "all"
                          ? "all"
                          : Number(e.target.value)
                      )
                    }
                  >
                    <option value="all">All categories</option>
                    {tpCategories.map((cat, i) => (
                      <option key={i} value={i}>
                        {cat}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {chartDisplayData.length === 0 ? (
                <div className="ems-empty">
                  <div className="ems-empty-icon">
                    <BookOpen size={26} />
                  </div>
                  <div style={{ fontWeight: 650 }}>No program data yet</div>
                  <div style={{ fontSize: 12.5, marginTop: 4 }}>
                    Admissions data will appear here once students are enrolled.
                  </div>
                </div>
              ) : (
                <ResponsiveContainer
                  width="100%"
                  height={
                    isNarrowChart
                      ? Math.max(280, chartDisplayData.length * 36 + 28)
                      : 400
                  }
                >
                  <BarChart
                    data={chartDisplayData}
                    layout={isNarrowChart ? "vertical" : "horizontal"}
                    margin={
                      isNarrowChart
                        ? { top: 8, right: 16, left: 4, bottom: 8 }
                        : { top: 12, right: 16, left: -10, bottom: 120 }
                    }
                    style={{ cursor: "pointer" }}
                  >
                    {isNarrowChart ? (
                      <XAxis
                        type="number"
                        allowDecimals={false}
                        tick={{
                          fontSize: 11,
                          fill: "var(--ems-faint)",
                          fontFamily: "Manrope, sans-serif",
                        }}
                      />
                    ) : (
                      <XAxis
                        dataKey="name"
                        interval={0}
                        tick={({ x, y, payload }) => {
                          const label = payload.value || "";
                          const maxChars = 22;
                          const display = label.length > maxChars ? label.slice(0, maxChars - 1) + "…" : label;
                          return (
                            <g transform={`translate(${x},${y})`}>
                              <text
                                x={0}
                                y={0}
                                dy={8}
                                transform="rotate(-40)"
                                textAnchor="end"
                                fill="var(--ems-muted)"
                                fontSize={11}
                                fontFamily="Manrope, sans-serif"
                              >
                                {display}
                              </text>
                            </g>
                          );
                        }}
                      />
                    )}
                    {isNarrowChart ? (
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={132}
                        interval={0}
                        tick={{
                          fontSize: 11,
                          fill: "var(--ems-muted)",
                          fontFamily: "Manrope, sans-serif",
                        }}
                        tickFormatter={(label) => {
                          const text = String(label || "");
                          return text.length > 18 ? `${text.slice(0, 17)}…` : text;
                        }}
                      />
                    ) : (
                      <YAxis
                        tick={{
                          fontSize: 12,
                          fill: "var(--ems-faint)",
                          fontFamily: "Manrope, sans-serif",
                        }}
                        allowDecimals={false}
                      />
                    )}
                    <RechartsTooltip
                      cursor={{ fill: "var(--ems-primary-tint-2)" }}
                      content={<TrainingProgramTooltip />}
                    />
                    <Bar
                      dataKey="total"
                      radius={[6, 6, 0, 0]}
                      maxBarSize={54}
                      onClick={(bar) => {
                        if (!bar?.name) return;
                        handleTrainingGraphNavigate(
                          bar.name,
                          trainingGraphCategory !== "all"
                            ? tpCategories[trainingGraphCategory]
                            : undefined,
                          bar.programId
                        );
                      }}
                    >
                      {chartDisplayData.map((_, i) => (
                        <Cell
                          key={i}
                          fill={`var(--ems-chart-${(i % 6) + 1})`}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </section>
        )}
      </div>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? theme.palette.background.default
            : "#ffffff",
        display: "flex",
      }}
    >
      <Box
        sx={{
          width: drawerOpen ? "280px" : "0px",
          height: "100vh",
          backgroundColor: theme.palette.background.paper,
          boxShadow:
            theme.palette.mode === "dark"
              ? "2px 0 10px rgba(255,255,255,0.1)"
              : "2px 0 10px rgba(0,0,0,0.1)",
          transition: "width 0.3s ease-in-out",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          position: "sticky",
          top: 0,
        }}
      >
        <Box sx={{ flex: 1, p: 2, minWidth: "280px" }}>
          {[
            {
              text: "Dashboard",
              icon: <BarChartIcon />,
            },
            {
              text: "Employees",
              icon: <PersonIcon />,
            },
            {
              text: "Reports",
              icon: <ShowChart />,
            },
            {
              text: "Calendar",
              icon: <CalendarToday />,
            },
          ].map((item) => (
            <Box
              key={item.text}
              sx={{
                display: "flex",
                alignItems: "center",
                p: 2,
                mb: 1,
                borderRadius: 2,
                cursor: "pointer",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.08)"
                      : "rgba(0, 0, 0, 0.04)",
                },
              }}
              onClick={() => handleMenuItemClick(item.text)}
            >
              <Box sx={{ mr: 2, color: "text.secondary" }}>{item.icon}</Box>
              <Typography variant="body1" sx={{ color: "text.primary" }}>
                {item.text}
              </Typography>
            </Box>
          ))}
          <Box
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              mt: 2,
              pt: 2,
            }}
          >
            {["Settings", "Help", "Logout"].map((item) => (
              <Box
                key={item}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 2,
                  mb: 1,
                  borderRadius: 2,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.08)"
                        : "rgba(0, 0, 0, 0.04)",
                  },
                }}
                onClick={() => handleMenuItemClick(item)}
              >
                <Box sx={{ mr: 2, color: "text.secondary" }}>
                  {item === "Settings" ? <SettingsIcon /> : <MoveToInbox />}
                </Box>
                <Typography variant="body1" sx={{ color: "text.primary" }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          p: 3,
          overflow: "auto",
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box>
              <Typography
                variant="h5"
                component="h2"
                sx={{ fontWeight: "semibold", color: "text.primary", mb: 1 }}
              >
                Dashboard
              </Typography>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                {formattedDate}
              </Typography>
            </Box>
          </Box>

          {Permission && Permission.includes("PERSONALIZE_DASHBOARD") && (
            <>
              <Button
                aria-controls={open ? "settings-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={handleClick}
                sx={{ minWidth: "auto", p: 1, borderRadius: "50%" }}
              >
                <SettingsIcon sx={{ fontSize: 28, color: "text.secondary" }} />
              </Button>

              <Menu
                id="settings-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleCloseMenu}
                MenuListProps={{
                  "aria-labelledby": "settings-button",
                }}
              >
                <MenuItem onClick={handleOpenPersonalizationPage}>
                  <DashboardCustomize sx={{ mr: 1 }} />
                  Personalize Dashboard
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>

        <Grid container spacing={3}>
            <hr style={{ border: 'none', borderTop: '3px solid #000' , width:"100%"}} />
          {(allWidgetConfigs.some(
            (w) => w.group === "Employees" && visibleWidgetIds.includes(w.id)
            
          ) ||
            allWidgetConfigs.some(
              (w) => w.group === "Occasions" && visibleWidgetIds.includes(w.id)
            ) ||
            allWidgetConfigs.some(
              (w) => w.group === "Total Workforce" && visibleWidgetIds.includes(w.id)
            ) ||
            allWidgetConfigs.some(
              (w) => w.group === "Interns" && visibleWidgetIds.includes(w.id)
            ) ||
            allWidgetConfigs.some(
              (w) =>
                w.group === "Freelancers" && visibleWidgetIds.includes(w.id)
            ) ||
            allWidgetConfigs.some(
              (w) =>
                w.group === "Time & Attendance" &&
                visibleWidgetIds.includes(w.id)
            ) ||
            allWidgetConfigs.some(
              (w) => w.group === "Leaves" && visibleWidgetIds.includes(w.id)
            )) && (
            <Grid container spacing={2}>
              
              {/* Employees */}
              {allWidgetConfigs
                .filter((w) => w.group === "Total Workforce")
                .some((w) => visibleWidgetIds.includes(w.id)) && (
                <Grid item xs={12} md={6}>
                  
                  <Typography
                  
                    variant="h6"
                    component="h2"
                    sx={{
                      fontWeight: "semibold",
                      color: "text.primary",
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    Total Workforce
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {allWidgetConfigs
                      .filter(
                        (w) =>
                          w.group === "Total Workforce" &&
                          visibleWidgetIds.includes(w.id)
                      )
                      .map((widget) => (
                        <Grid item xs={12} sm={12} md={6} key={widget.id}>
                          <Card
                            sx={{
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "#ebebeb",
                              backdropFilter: "blur(4px)",
                              border: "none",
                              boxShadow: 3,
                              transition: "all 0.3s ease-in-out",
                              "&:hover": {
                                boxShadow: 6,
                                transform: "scale(1.02)",
                              },
                              height: "160px",
                              width: { xs: "80vw", sm: "50vw", lg: "17vw" },
                              margin: "0 auto",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              navigate(widget.link)
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleWidgetClick(widget.id, widget.title);
                              }
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              
                              {widget.id === "totalworkforce" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip title={widget.title} placement="top">
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      {widget.title}
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    Total
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : widget.id === "totalEmployees" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip title="Total Employess" placement="top">
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      Employees
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    Total
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : widget.id === "totalFreelancers" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip title="Total Employess" placement="top">
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      Freelancers
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    Total
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        color: "text.secondary",
                                        fontWeight: "medium",
                                        mb: 2,
                                      }}
                                    >
                                      {widget.title}
                                    </Typography>
                                    <Typography
                                      variant="h4"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                      }}
                                    >
                                      {widget.value}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                  </Grid>
                </Grid>
              )}
              {/* Employees */}
              {allWidgetConfigs
                .filter((w) => w.group === "Employees")
                .some((w) => visibleWidgetIds.includes(w.id)) && (
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                      fontWeight: "semibold",
                      color: "text.primary",
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    Employees
                  </Typography>
                  <Grid container spacing={2}>
                    {allWidgetConfigs
                      .filter(
                        (w) =>
                          w.group === "Employees" &&
                          visibleWidgetIds.includes(w.id)
                      )
                      .map((widget) => (
                        <Grid item xs={12} sm={12} md={6} key={widget.id}>
                          <Card
                            sx={{
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "#ebebeb",
                              backdropFilter: "blur(4px)",
                              border: "none",
                              boxShadow: 3,
                              transition: "all 0.3s ease-in-out",
                              "&:hover": {
                                boxShadow: 6,
                                transform: "scale(1.02)",
                              },
                              height: "160px",
                              width: { xs: "80vw", sm: "50vw", lg: "17vw" },
                              margin: "0 auto",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              navigate(widget.link)
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleWidgetClick(widget.id, widget.title);
                              }
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              {widget.id === "totalEmployees" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip title={widget.title} placement="top">
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      {widget.title}
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    Total
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : widget.id === "newJoiningThisMonth" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip title="New Joining" placement="top">
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      New Joining
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    This Month
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        color: "text.secondary",
                                        fontWeight: "medium",
                                        mb: 2,
                                      }}
                                    >
                                      {widget.title}
                                    </Typography>
                                    <Typography
                                      variant="h4"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                      }}
                                    >
                                      {widget.value}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                  </Grid>
                </Grid>
              )}

              {/* Occasions */}
              {allWidgetConfigs
                .filter((w) => w.group === "Occasions")
                .some((w) => visibleWidgetIds.includes(w.id)) && (
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                      fontWeight: "semibold",
                      color: "text.primary",
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    Occasions
                  </Typography>
                  <Grid container spacing={2}>
                    {allWidgetConfigs
                      .filter(
                        (w) =>
                          w.group === "Occasions" &&
                          visibleWidgetIds.includes(w.id)
                      )
                      .map((widget) => (
                        <Grid item xs={12} sm={12} key={widget.id}>
                          <Card
                            sx={{
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "#ebebeb",
                              backdropFilter: "blur(4px)",
                              border: "none",
                              boxShadow: 3,
                              transition: "all 0.3s ease-in-out",
                              "&:hover": {
                                boxShadow: 6,
                                transform: "scale(1.02)",
                              },
                              height: "160px",
                              width: { xs: "80vw", sm: "50vw", lg: "17vw" },
                              margin: "0 auto",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              navigate(widget.link)
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleWidgetClick(widget.id, widget.title);
                              }
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              {widget.id === "birthdayThisMonth" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip title="Birthday" placement="top">
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      Birthday
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    This Month
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : widget.id === "workAnniversariesThisMonth" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip
                                    title="Work Anniversaries"
                                    placement="top"
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      Work Anniversaries
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    This Month
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        color: "text.secondary",
                                        fontWeight: "medium",
                                        mb: 2,
                                      }}
                                    >
                                      {widget.title}
                                    </Typography>
                                    <Typography
                                      variant="h4"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                      }}
                                    >
                                      {widget.value}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                  </Grid>
                </Grid>
              )}

              {/* Interns - UPDATED with proper functionality */}
              {allWidgetConfigs
                .filter((w) => w.group === "Interns")
                .some((w) => visibleWidgetIds.includes(w.id)) && (
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                      fontWeight: "semibold",
                      color: "text.primary",
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    Interns
                  </Typography>
                  <Grid container spacing={2}>
                    {allWidgetConfigs
                      .filter(
                        (w) =>
                          w.group === "Interns" &&
                          visibleWidgetIds.includes(w.id)
                      )
                      .map((widget) => (
                        <Grid item xs={12} sm={12} md={6} key={widget.id}>
                          <Card
                            sx={{
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "#ebebeb",
                              backdropFilter: "blur(4px)",
                              border: "none",
                              boxShadow: 3,
                              transition: "all 0.3s ease-in-out",
                              "&:hover": {
                                boxShadow: 6,
                                transform: "scale(1.02)",
                              },
                              height: "160px",
                              width: { xs: "80vw", sm: "50vw", lg: "17vw" },
                              margin: "0 auto",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              handleWidgetClick(widget.id, widget.title)
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleWidgetClick(widget.id, widget.title);
                              }
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              {widget.id === "totalInterns" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip title={widget.title} placement="top">
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      {widget.title}
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    Total
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : widget.id === "newJoiningThisMonthInterns" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip title="New Joining" placement="top">
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      New Joining
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    This Month
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        color: "text.secondary",
                                        fontWeight: "medium",
                                        mb: 2,
                                      }}
                                    >
                                      {widget.title}
                                    </Typography>
                                    <Typography
                                      variant="h4"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                      }}
                                    >
                                      {widget.value}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                  </Grid>
                </Grid>
              )}


              {/* Freelancers */}
              {allWidgetConfigs
                .filter((w) => w.group === "Freelancers")
                .some((w) => visibleWidgetIds.includes(w.id)) && (
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                      fontWeight: "semibold",
                      color: "text.primary",
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    Freelancers
                  </Typography>
                  <Grid container spacing={2}>
                    {allWidgetConfigs
                      .filter(
                        (w) =>
                          w.group === "Freelancers" &&
                          visibleWidgetIds.includes(w.id)
                      )
                      .map((widget) => (
                        <Grid item xs={12} sm={12} md={6} key={widget.id}>
                          <Card
                            sx={{
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "#ebebeb",
                              backdropFilter: "blur(4px)",
                              border: "none",
                              boxShadow: 3,
                              transition: "all 0.3s ease-in-out",
                              "&:hover": {
                                boxShadow: 6,
                                transform: "scale(1.02)",
                              },
                              height: "160px",
                              width: { xs: "80vw", sm: "50vw", lg: "17vw" },
                              margin: "0 auto",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              navigate(widget.link)
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ")
                                handleWidgetClick(widget.id, widget.title);
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              {widget.id === "totalFreelancers" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip title={widget.title} placement="top">
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      {widget.title}
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    Total
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : widget.id ===
                                "newJoiningThisMonthFreelancers" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip title="New Joining" placement="top">
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      New Joining
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    This Month
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        color: "text.secondary",
                                        fontWeight: "medium",
                                        mb: 2,
                                      }}
                                    >
                                      {widget.title}
                                    </Typography>
                                    <Typography
                                      variant="h4"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                      }}
                                    >
                                      {widget.value}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                  </Grid>
                </Grid>
              )}

              {/* Time & Attendance and Leaves */}
              {(allWidgetConfigs
                .filter((w) => w.group === "Time & Attendance")
                .some((w) => visibleWidgetIds.includes(w.id)) ||
                allWidgetConfigs
                  .filter((w) => w.group === "Leaves")
                  .some((w) => visibleWidgetIds.includes(w.id))) && (
                <Grid container spacing={2} sx={{ mt: 0 }}>
                  {allWidgetConfigs
                    .filter((w) => w.group === "Time & Attendance")
                    .some((w) => visibleWidgetIds.includes(w.id)) && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="h6"
                        component="h2"
                        sx={{
                          fontWeight: "semibold",
                          color: "text.primary",
                          mb: 3,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        Time & Attendance
                      </Typography>
                      <Grid container spacing={2}>
                        {allWidgetConfigs
                          .filter(
                            (w) =>
                              w.group === "Time & Attendance" &&
                              visibleWidgetIds.includes(w.id)
                          )
                          .map((widget) => (
                            <Grid item xs={12} sm={12} md={6} key={widget.id}>
                              <Card
                                sx={{
                                  bgcolor:
                                    theme.palette.mode === "dark"
                                      ? "rgba(255, 255, 255, 0.05)"
                                      : "#ebebeb",
                                  backdropFilter: "blur(4px)",
                                  border: "none",
                                  boxShadow: 3,
                                  transition: "all 0.3s ease-in-out",
                                  "&:hover": {
                                    boxShadow: 6,
                                    transform: "scale(1.02)",
                                  },
                                  height: "160px",
                                  width: { xs: "80vw", sm: "50vw", lg: "17vw" },
                                  margin: "0 auto",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  handleWidgetClick(widget.id, widget.title)
                                }
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    handleWidgetClick(widget.id, widget.title);
                                  }
                                }}
                              >
                                <CardContent sx={{ p: 3 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <Box>
                                      <Typography
                                        variant="subtitle1"
                                        sx={{
                                          color: "text.secondary",
                                          fontWeight: "medium",
                                          mb: 2,
                                        }}
                                      >
                                        {widget.title}
                                      </Typography>
                                      <Typography
                                        variant="h4"
                                        sx={{
                                          fontWeight: "semibold",
                                          color: "text.primary",
                                        }}
                                      >
                                        {widget.value}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                      </Grid>
                    </Grid>
                  )}

                  {allWidgetConfigs
                    .filter((w) => w.group === "Leaves")
                    .some((w) => visibleWidgetIds.includes(w.id)) && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="h6"
                        component="h2"
                        sx={{
                          fontWeight: "semibold",
                          color: "text.primary",
                          mb: 3,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        Leaves
                      </Typography>
                      <Grid container spacing={2}>
                        {allWidgetConfigs
                          .filter(
                            (w) =>
                              w.group === "Leaves" &&
                              visibleWidgetIds.includes(w.id)
                          )
                          .map((widget) => (
                            <Grid item xs={12} sm={12} md={6} key={widget.id}>
                              <Card
                                sx={{
                                  bgcolor:
                                    theme.palette.mode === "dark"
                                      ? "rgba(255, 255, 255, 0.05)"
                                      : "#ebebeb",
                                  backdropFilter: "blur(4px)",
                                  border: "none",
                                  boxShadow: 3,
                                  transition: "all 0.3s ease-in-out",
                                  "&:hover": {
                                    boxShadow: 6,
                                    transform: "scale(1.02)",
                                  },
                                  height: "160px",
                                  width: { xs: "80vw", sm: "50vw", lg: "17vw" },
                                  margin: "0 auto",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  handleWidgetClick(widget.id, widget.title)
                                }
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    handleWidgetClick(widget.id, widget.title);
                                  }
                                }}
                              >
                                <CardContent sx={{ p: 3 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <Box>
                                      <Typography
                                        variant="subtitle1"
                                        sx={{
                                          color: "text.secondary",
                                          fontWeight: "medium",
                                          mb: 2,
                                        }}
                                      >
                                        {widget.title}
                                      </Typography>
                                      <Typography
                                        variant="h4"
                                        sx={{
                                          fontWeight: "semibold",
                                          color: "text.primary",
                                        }}
                                      >
                                        {widget.value}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>
          )}

          {allChartConfigs.some((chart) =>
            visibleChartIds.includes(chart.id)
          ) && (
            <Box sx={{ mt: 6 }}>
                <hr style={{ border: 'none', borderTop: '3px solid #000' , width:"100%"}} />
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  fontWeight: "semibold",
                  color: "text.primary",
                  mb: 4,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <ShowChart sx={{ fontSize: 28, color: "#4F46E5" }} />
                Employee Distribution Analytics
              </Typography>
              
              <Grid container spacing={2}>
                {allChartConfigs
                  .filter((chart) => visibleChartIds.includes(chart.id))
                  .map((chart) => (
                    <Grid item lg={6} sm={12} key={chart.id}>
                      <Card
                        sx={{
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.05)"
                              : "#ebebeb",
                          backdropFilter:
                            chartViewModes[chart.id] === "chart"
                              ? "blur(4px)"
                              : "none",
                          border: "none",
                          boxShadow: 3,
                          width: {
                            xs: "80vw",
                            sm: "80vw",
                            md: "80vw",
                            lg: "43vw",
                          },
                          height: "450px",
                          display: "flex",
                          flexDirection: "column",
                          "&:hover": {
                            boxShadow: 6,
                            transform:
                              chartViewModes[chart.id] === "chart"
                                ? "scale(1.02)"
                                : "none",
                          },
                        }}
                      >
                        <CardContent
                          sx={{
                            p: 3,
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: "semibold",
                              color: "text.primary",
                              mb: 2,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {chart.icon &&
                              React.createElement(chart.icon, {
                                sx: { fontSize: 20, color: chart.color },
                              })}
                            {chart.title}
                          </Typography>

                          {chartViewModes[chart.id] === "chart" ? (
                            <Box
                              sx={{
                                flex: 1,
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: 240,
                                borderRadius: 1,
                                border: "1px dashed",
                                borderColor: "divider",
                                bgcolor: "background.paper",
                                p: 2,
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                align="center"
                              >
                                Charts are unavailable. Install{" "}
                                <code>@mui/x-charts</code> to enable this section.
                              </Typography>
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                flex: 1,
                                width: "100%",
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <DataGrid
                                rowBuffer={2}
                                rowThreshold={0}
                                sx={{
                                  flex: 1,
                                  "& .MuiDataGrid-main": {
                                    height: "100%",
                                  },
                                }}
                                rows={chart.data.map((row, index) => ({
                                  id: `${chart.id}-${index}`,
                                  label: row.label || row.name,
                                  value: row.value,
                                }))}
                                columns={[
                                  {
                                    field: "label",
                                    headerName:
                                      chart.type === "pie" ? "Label" : "Name",
                                    flex: 1,
                                    headerAlign: "center",
                                    align: "center",
                                  },
                                  {
                                    field: "value",
                                    headerName: "Value",
                                    type: "number",
                                    flex: 1,
                                    headerAlign: "center",
                                    align: "center",
                                  },
                                ]}
                                pageSizeOptions={[5, 10, 25]}
                                initialState={{
                                  pagination: {
                                    paginationModel: { pageSize: 5 },
                                  },
                                }}
                                disableRowSelectionOnClick
                              />
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            </Box>
          )}
        </Grid>
      </Box>
    </Box>
  );
}
